import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import useLanguage from '../hooks/useLanguage';
import UnitStatusPanel from './UnitStatusPanel';
import secureApiService, { secureApi } from '../services/secureApi';
import LockStatusPanel from './LockStatusPanel';
import ErrorBoundary from './ErrorBoundary';
import axios from 'axios';
import '../styles/PropertyDetailsStyled.css';
import BookingModal from './BookingModal';
import OwnerTaskDashboard from './OwnerTaskDashboard';
import { getRoomTypes, getChannexRoomMapping, getRoomTypesWithRatePlans, getEffectiveRoomMapping } from '../utils/roomTypeHelper';
import { formatCompactDate, formatDate as formatLongDate, formatDateTime, formatDateWithDay, formatShortDate } from '../utils/dateFormatter';
import { getOtaCategoryLabel } from '../utils/otaReviewCategories';
import { useBookings } from '../hooks/useBookings';
import { getPlatformLogo, getPlatformDisplayName, getPlatformIcon, matchesPlatformFilter, getPlatformColor } from '../utils/platformUtils';
import { collectBookingLinks, formatBookingLinksForSharing } from '../utils/publicBookingLinks';
import {
  AVAILABILITY_STATE,
  CLOSED_NIGHT_LINE,
  availabilityView,
  isClosedState,
  resolveAvailabilityState
} from '../utils/availabilityUiHelpers';
import { DATE_RANGE_OPTIONS, DATE_RANGE_ALL, DATE_RANGE_CUSTOM, filterBookingsByDateRange } from '../utils/bookingDateRange';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import moment from 'moment-timezone';
import { BUSINESS_TIMEZONE } from '../config/timezoneConfig';
import { CORE_STAFF_ROLES } from '../config/roleConfig';
import { riyadhDayIso } from '../utils/dateValueHelper';
import { Modal } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import { QR_ERROR_CORRECTION, QR_QUIET_ZONE_MODULES, toPrintableSvg, downloadSvg } from '../utils/plaqueQr';

// Platform colours come from utils/platformUtils.js (getPlatformColor) — the same list the
// availability grid «التقويم» uses, so a booking wears one colour on both calendars.

// ===== OTA reviews (read-only display of stored Airbnb/Booking.com reviews) =====

// Brand label (Latin per convention) + accent colour, keyed off the stored `source`.
const OTA_BRAND = {
  airbnb: { label: 'Airbnb', color: '#FF5A5F' },
  booking: { label: 'Booking.com', color: '#003580' },
  expedia: { label: 'Expedia', color: '#FFC72C' },
  vrbo: { label: 'Vrbo', color: '#3D67FF' },
  gathern: { label: 'Gathern', color: '#E8730C' },
  manual: { label: 'يدوي', color: '#471396' },
  other: { label: 'منصة أخرى', color: '#471396' }
};
const getOtaBrand = (source) => OTA_BRAND[source] || OTA_BRAND.other;


// Render a /10 score as 5 stars (presentation-only ÷2 — the stored fact stays /10).
const renderStarsFromTen = (scoreOutOfTen) => {
  const outOfFive = Math.max(0, Math.min(5, (Number(scoreOutOfTen) || 0) / 2));
  const full = Math.floor(outOfFive);
  const frac = outOfFive - full;
  const roundedUp = frac >= 0.75;
  const hasHalf = frac >= 0.25 && frac < 0.75;
  return [...Array(5)].map((_, i) => {
    let cls = 'bi bi-star';
    if (i < full || (roundedUp && i === full)) cls = 'bi bi-star-fill';
    else if (hasHalf && i === full) cls = 'bi bi-star-half';
    return <i key={i} className={cls} style={{ color: '#FFD700' }}></i>;
  });
};

// Card for a single stored OTA review. Read-only, except the optional «إخفاء من الموقع» switch for
// core staff (tasks/public-listings-reviews.md R11) — it affects book.hostme.sa only.
const OtaReviewCard = ({ review, onToggleWebsite }) => {
  const brand = getOtaBrand(review.source);
  const hasScore = review.overallScore != null;
  const outOfFive = hasScore ? ((Number(review.overallScore) || 0) / 2).toFixed(1) : null;
  return (
    <div className="card" style={{ borderRight: `4px solid ${brand.color}` }}>
      <div className="card-body">
        {/* Header: guest, date, score + brand badge */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 className="mb-1" dir="auto">{review.guestName || 'ضيف'}</h5>
            <small className="text-muted">{formatShortDate(review.sourceReceivedAt)}</small>
            {hasScore && (
              <div className="mt-1 d-flex align-items-center gap-2" style={{ flexWrap: 'wrap' }}>
                <span>{renderStarsFromTen(review.overallScore)}</span>
                <span style={{ fontSize: '0.8rem', color: '#666' }}>
                  {outOfFive} <span style={{ color: '#aaa' }}>({review.overallScore}/10)</span>
                </span>
              </div>
            )}
          </div>
          <span className="badge" style={{ backgroundColor: brand.color, color: '#fff', fontSize: '0.8rem' }}>
            {brand.label}
          </span>
        </div>

        {/* Airbnb: the guest's review stays hidden until WE review the guest, or day 14 (O10) */}
        {review.isHidden && (
          <div className="mb-2" style={{ fontSize: '0.8rem', color: '#b8860b' }}>
            <i className="bi bi-eye-slash me-1"></i>
            مخفي على {brand.label} حتى نقيّم الضيف أو تمضي ١٤ يوماً على المغادرة
          </div>
        )}

        {/* Original-language content, as received */}
        {review.content && (
          <p className="mb-3" style={{ lineHeight: '1.6' }} dir="auto">{review.content}</p>
        )}

        {/* Private feedback — HOST-EYES-ONLY, core staff only. Sits directly after the
            public text so the guest's two voices read together, before the host reply.
            The backend decides who receives this field (decorateReview in
            otaReviewRoutes.js); an owner never gets it, so absence = nothing renders.
            Styled as a confidential sibling of the host-reply inset: same shape, ochre
            accent already used by this card's "hidden on platform" note. */}
        {review.privateFeedback && (
          <div
            className="mb-3 hide-print"
            style={{
              padding: '12px',
              backgroundColor: '#fffaf0',
              borderInlineStart: '3px solid #b8860b',
              borderRadius: '4px'
            }}
          >
            <div className="d-flex align-items-center gap-2 mb-2" style={{ flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#7a5f0a' }}>
                <i className="bi bi-lock-fill me-1"></i>
                ملاحظة خاصة من الضيف
              </span>
              <span
                className="badge"
                style={{ backgroundColor: '#7a5f0a', color: '#fff', fontSize: '0.7rem', fontWeight: 500 }}
              >
                للفريق الداخلي فقط
              </span>
            </div>
            <p
              style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.6', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
              dir="auto"
            >
              {review.privateFeedback}
            </p>
            <div style={{ marginTop: '6px', fontSize: '0.75rem', color: '#6b5309' }}>
              لا تظهر في التقييم العام على {brand.label}
            </div>
          </div>
        )}

        {/* Category breakdown */}
        {Array.isArray(review.categoryScores) && review.categoryScores.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            {review.categoryScores.map((c, i) => (
              <span key={i} className="badge bg-light text-dark" style={{ fontWeight: 500, border: '1px solid #eee' }}>
                {getOtaCategoryLabel(c.category)}: {c.score}/10
              </span>
            ))}
          </div>
        )}

        {/* Host reply (read-only — replies happen on the platform) */}
        {review.reply && (
          <div style={{
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRight: '3px solid #007bff',
            borderRadius: '4px'
          }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '6px' }}>
              رد المضيف:
              {review.replyUpdatedAt && (
                <span style={{ fontWeight: 400, color: '#6c757d' }}> {formatShortDate(review.replyUpdatedAt)}</span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: '0.9rem' }} dir="auto">{review.reply}</p>
          </div>
        )}

        {/* Public units page switch — staff only (the backend omits the handler's field for owners). */}
        {onToggleWebsite && (
          <div className="d-flex align-items-center justify-content-end gap-2 mt-3 hide-print" style={{ flexWrap: 'wrap' }}>
            {review.hiddenFromWebsite && (
              <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                <i className="bi bi-eye-slash me-1"></i>
                مخفي من صفحة الوحدات العامة
              </span>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onToggleWebsite(review)}
            >
              {review.hiddenFromWebsite ? 'إظهار على الموقع' : 'إخفاء من الموقع'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to get bookings for a specific date (kept for potential future use)
// eslint-disable-next-line no-unused-vars
const getBookingsForDate = (bookings, targetDate) => {
  if (!bookings || !bookings.length) return [];

  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0);

  return bookings.filter(booking => {
    const checkIn = new Date(booking.checkIn);
    const checkOut = new Date(booking.checkOut);
    checkIn.setHours(0, 0, 0, 0);
    checkOut.setHours(0, 0, 0, 0);

    // Booking overlaps with this date if:
    // - Check-in is on this date OR
    // - Check-out is on this date OR
    // - This date is between check-in and check-out
    return target >= checkIn && target < checkOut;
  });
};

// Helper function to determine the color of the occupancy gauge
// eslint-disable-next-line no-unused-vars
const getOccupancyColor = (rate) => {
  const numRate = parseFloat(rate);
  if (numRate < 30) return '#F44336'; // Red
  if (numRate < 60) return '#FFC107'; // Yellow
  return '#4CAF50'; // Green
};

// Format date to Arabic format using centralized formatter
const formatDate = (dateString) => {
  return formatCompactDate(dateString);
};

// Calculate number of nights between two dates
const calculateNights = (startDate, endDate) => {
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  } catch (error) {
    return 0;
  }
};

// Translate maintenance type to Arabic
// eslint-disable-next-line no-unused-vars
const translateMaintenanceType = (type) => {
  switch (type) {
    case 'cleaning':
      return 'تنظيف';
    case 'repair':
      return 'صيانة';
    case 'inspection':
      return 'فحص';
    case 'other':
      return 'أخرى';
    default:
      return type;
  }
};


const SecurePropertyDetails = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [property, setProperty] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const { t: lockT, language } = useLanguage();
  const initialTab = (() => {
    // Allow deep-linking to a tab via ?tab=<name> — used by the dashboard
    // locks table to jump directly into a property's Locks tab.
    const params = new URLSearchParams(location.search);
    const requested = params.get('tab');
    return requested || 'calendar';
  })();
  // …and to a specific month via ?date=YYYY-MM-DD, so the pricing page's 30-day list
  // can open the calendar on the night ops was actually looking at. Parsed at UTC noon:
  // a bare 'YYYY-MM-DD' is UTC midnight, which is the PREVIOUS day in any timezone west
  // of Greenwich, and noon lands on the intended day everywhere.
  //
  // ⚠️ This is the VIEW date only — it must never become `calendarCurrentDate`, which
  // anchors the 365-day availability fetch (`fetchCalendarData`). Moving that origin
  // forward leaves every day BEFORE the deep-linked date outside the fetched window, so
  // paging back renders real nights with no rate or availability on them. The data
  // window stays at today; only the month on screen moves.
  const initialCalendarViewDate = (() => {
    const requested = new URLSearchParams(location.search).get('date');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(requested || '')) return null;
    const parsed = new Date(`${requested}T12:00:00Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [channexMapping, setChannexMapping] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [syncMessage, setSyncMessage] = useState('');
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Door-plaque link (tasks/checkout-plaque-panel.md). Fetched from its own endpoint, never from
  // the property payload — the token is `select: false` and stays that way.
  // Collapsed by default and fetched only when opened: this link is needed ONCE per unit, when a
  // plaque is commissioned. Requesting a credential on every visit to a daily page would be a
  // request nobody asked for, on the one field in the model that is deliberately hard to read.
  const [plaqueOpen, setPlaqueOpen] = useState(false);
  const [plaqueLink, setPlaqueLink] = useState(null);
  const [plaqueError, setPlaqueError] = useState(null);
  const [plaqueCopied, setPlaqueCopied] = useState(false);
  // The QR is rendered off-screen and serialised on demand (tasks/checkout-plaque-artwork-generator.md).
  // Reading it back out of the DOM rather than re-encoding it means the file the designer receives is
  // byte-for-byte the symbol this screen produced — there is no second encoder to disagree with.
  const [plaqueQrError, setPlaqueQrError] = useState(false);
  const plaqueQrRef = useRef(null);

  // Public booking links (tasks/property-public-booking-links.md). Read straight off the property
  // payload — they are public URLs, not credentials, so there is nothing to fetch on demand.
  const [bookingLinksOpen, setBookingLinksOpen] = useState(false);
  const [copiedLinkKey, setCopiedLinkKey] = useState(null);
  const bookingLinksRef = useRef(null);

  // Use React Query hook for bookings (with caching)
  const {
    bookings: mvpBookings,
    refetch: refetchBookings,
    isLoading: bookingsLoading,
    error: bookingsError
  } = useBookings({
    propertyId: propertyId
  });

  // Derived state: Filter out cancelled bookings for calendar display
  // Calendar should only show active/confirmed bookings to prevent confusion
  // (Bookings tab uses mvpBookings with its own filters to show all statuses)
  const activeBookingsForCalendar = useMemo(() => {
    return mvpBookings.filter(booking => booking.status !== 'cancelled');
  }, [mvpBookings]);

  // Test 1 Certification state
  const [syncData, setSyncData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [syncTaskIds, setSyncTaskIds] = useState(null);
  const [syncProgress, setSyncProgress] = useState(0);
  const [basePrice, setBasePrice] = useState(400); // Base price for full sync (single property)
  const [basePrices, setBasePrices] = useState({}); // Multiple base prices for hotels
  const [weekendIncrease, setWeekendIncrease] = useState(0); // Weekend increase percentage (default 0%)
  // const [certificationMode, setCertificationMode] = useState(false);
  
  // Rate Management state
  // Note: Individual rate updates removed - use bulk modal for all price changes
  
  // Rates from RateMaster
  const [rates, setRates] = useState({});
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState(null);
  
  // Property type detection - used throughout component
  const isApartment = ['شقة', 'فيلا', 'غرفة'].includes(property?.type);
  
  // Room name editing state
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editingRoomName, setEditingRoomName] = useState('');
  const [roomUpdateStatus, setRoomUpdateStatus] = useState('idle');

  // «إيقاف الوحدة / استئناف» — same people as the old «حالة التشغيل» dropdown (admin, ops, ops-manager);
  // the backend enforces the same role group. The status itself is derived — see UnitStatusPanel.
  const canUpdateOperationalStatus = useMemo(
    () => user?.role === 'admin' || user?.role === 'ops' || user?.role === 'ops-manager',
    [user]
  );

  // ====== Contract start date (tasks/property-contract-onboarded-dates.md steps 5-6) ======
  // Reference data, edited perhaps once in a unit's life — so it reads as text and only becomes an
  // input when someone asks. A permanently-open date field in this header would be noise for a
  // value nobody changes, which is why it does not copy the always-visible operational-status
  // control above.
  const [editingContractStart, setEditingContractStart] = useState(false);
  const [contractStartDraft, setContractStartDraft] = useState('');
  const [savingContractStart, setSavingContractStart] = useState(false);
  const [contractStartError, setContractStartError] = useState('');

  // D5: core staff set this date; the owner reads it. The start date of the agreement we bill
  // against is not the owner's to edit.
  const canEditContractStart = CORE_STAFF_ROLES.includes(user?.role);

  const startContractStartEdit = () => {
    // ⚠️ `riyadhDayIso`, NOT `toInputDateValue`. The stored value is UTC midnight (D8) and
    // `toInputDateValue` is browser-local: on any machine west of UTC it hands the input the
    // PREVIOUS day, so opening the editor and saving without touching anything would silently
    // walk the date backwards one day per edit.
    setContractStartDraft(riyadhDayIso(property?.contractStartDate) || '');
    setContractStartError('');
    setEditingContractStart(true);
  };

  const cancelContractStartEdit = () => {
    setEditingContractStart(false);
    setContractStartError('');
  };

  const saveContractStart = async () => {
    if (!propertyId) return;
    setSavingContractStart(true);
    setContractStartError('');
    try {
      // Empty clears it — «we do not know» has to stay expressible (D2).
      const response = await secureApi.patch(`/api/v1/properties/${propertyId}/contract-start`, {
        contractStartDate: contractStartDraft || null
      });
      setProperty(prev => ({ ...prev, contractStartDate: response.data.contractStartDate }));
      setEditingContractStart(false);
    } catch (error) {
      console.error('Failed to update contract start date:', error);
      setContractStartError(error.response?.data?.error || 'تعذر حفظ التاريخ. حاول مرة أخرى.');
    } finally {
      setSavingContractStart(false);
    }
  };

  // Rate plan name editing state
  const [editingRatePlanId, setEditingRatePlanId] = useState(null);
  const [editingRatePlanName, setEditingRatePlanName] = useState('');
  const [ratePlanUpdateStatus, setRatePlanUpdateStatus] = useState('idle');
  
  // Room types and rate plans sync state
  const [syncRoomTypesStatus, setSyncRoomTypesStatus] = useState('idle');
  const [syncRoomTypesMessage, setSyncRoomTypesMessage] = useState('');
  
  // Bulk rate update state
  const [showBulkRateModal, setShowBulkRateModal] = useState(false);
  const [bulkRatesList, setBulkRatesList] = useState([]);

  // Availability management modal state
  const [availabilityList, setAvailabilityList] = useState([]);
  const [availabilityUpdateStatus, setAvailabilityUpdateStatus] = useState('idle');

  // Combined modal active tab state
  const [activeModalTab, setActiveModalTab] = useState('pricing'); // 'pricing' or 'availability'
  
  // Calendar data state
  // eslint-disable-next-line no-unused-vars
  const [calendarData, setCalendarData] = useState({}); // Old calendar data (kept for potential grid view)
  const [calendarBookings, setCalendarBookings] = useState([]); // Store bookings for calendar
  // eslint-disable-next-line no-unused-vars
  const [calendarLoading, setCalendarLoading] = useState(false); // Old calendar loading (kept for potential grid view)
  // A failed availability read must never look like free days (tasks/owner-screens-mobile.md step 7).
  const [calendarError, setCalendarError] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [calendarWeekStart, setCalendarWeekStart] = useState(new Date()); // Old week navigation (kept for potential grid view)
  // eslint-disable-next-line no-unused-vars
  const [calendarCurrentDate, setCalendarCurrentDate] = useState(new Date()); // Anchors the 365-day availability fetch — always today, never the ?date= view date
  // eslint-disable-next-line no-unused-vars
  const [showViewDropdown, setShowViewDropdown] = useState(false);

  // Day details modal state
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState(null);
  
  // Booking sync state
  const [bookingSyncStatus, setBookingSyncStatus] = useState('idle');
  const [bookingSyncMessage, setBookingSyncMessage] = useState('');

  // Bookings filter state - default to upcoming (confirmed) bookings
  const [bookingsFilters, setBookingsFilters] = useState({
    status: 'upcoming',
    platform: '',
    dateRange: DATE_RANGE_ALL,
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [filteredBookings, setFilteredBookings] = useState([]);

  // Reviews state (Admin/Ops only)
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewFilters, setReviewFilters] = useState({
    status: 'pending', // pending, approved, rejected, all
    rating: '',
    search: ''
  });
  const [selectedReview, setSelectedReview] = useState(null);
  const [showReviewResponseModal, setShowReviewResponseModal] = useState(false);
  const [reviewResponse, setReviewResponse] = useState('');
  const [reviewActionLoading, setReviewActionLoading] = useState(false);

  // OTA reviews (read-only — Airbnb / Booking.com via stored ota_reviews)
  const [reviewSubTab, setReviewSubTab] = useState('ota'); // 'ota' | 'direct' — OTA shown first
  const [otaReviews, setOtaReviews] = useState([]);
  const [otaReviewsLoading, setOtaReviewsLoading] = useState(false);
  const [otaReviewsError, setOtaReviewsError] = useState(null);
  const [otaVisibilityError, setOtaVisibilityError] = useState(null);
  const [otaTotal, setOtaTotal] = useState(0);
  const [otaSourceFilter, setOtaSourceFilter] = useState('all'); // 'all' | OTA source

  // Distinct OTA sources present in the loaded reviews (drives the per-OTA filter pills)
  const otaAvailableSources = useMemo(() => {
    const order = ['airbnb', 'booking', 'expedia', 'vrbo', 'gathern', 'manual', 'other'];
    return [...new Set(otaReviews.map(r => r.source))].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }, [otaReviews]);
  // Ignore a stale filter that no longer exists after a reload (e.g. switched property)
  const otaEffectiveFilter = (otaSourceFilter !== 'all' && otaAvailableSources.includes(otaSourceFilter)) ? otaSourceFilter : 'all';
  const filteredOtaReviews = otaEffectiveFilter === 'all' ? otaReviews : otaReviews.filter(r => r.source === otaEffectiveFilter);

  // Calendar cell editing state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCell, setEditingCell] = useState({
    type: '', // 'availability', 'minStay', 'rate'
    roomTypeId: '',
    roomTypeName: '',
    dayIndex: 0,
    date: '',
    currentValue: '',
    ratePlanId: ''
  });
  const [editValue, setEditValue] = useState('');
  const [bulkRateForm, setBulkRateForm] = useState({
    roomTypeId: '',
    ratePlanId: '',
    date: '',
    dateEnd: '', // For date range support
    updateType: 'single', // 'single', 'range', or 'baseRate'
    applyToAllRatePlans: false, // Apply to all rate plans option
    // Direct field values (simplified - no checkboxes)
    rate: '',
    minStayArrival: '',  // Empty string when not set
    maxStay: '',  // Empty string when not set
    stopSellAction: 'none',  // Stop sell action: 'none' | 'allow' | 'block'
    selectedDays: [0, 1, 2, 3, 4, 5, 6]  // All days by default (0=Sunday, 6=Saturday)
  });
  const [bulkRateStatus, setBulkRateStatus] = useState('idle');
  
  // Availability form state
  const [availabilityForm, setAvailabilityForm] = useState({
    date: '',
    dateEnd: '', // For date range support
    availableRooms: '',
    updateType: 'single' // 'single' or 'range'
    // roomTypeId removed - always use default for apartments
  });
  
  // Channel detection state
  const [channelInfo, setChannelInfo] = useState(null);
  const [isAirbnbProperty, setIsAirbnbProperty] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [channelConstraints, setChannelConstraints] = useState({
    singleRatePlan: false,
    maxRatePlans: null
  });

  // Rate plans sync state
  // eslint-disable-next-line no-unused-vars
  const [ratePlansSyncing, setRatePlansSyncing] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [ratePlansError, setRatePlansError] = useState(null);
  
  // Function to sync room types and rate plans from Channex
  const syncRoomTypesAndRatePlans = async () => {
    try {
      setRatePlansSyncing(true);
      setRatePlansError(null);

      // Use axios directly since secureApiService doesn't have a generic post method
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL || ''}/api/v1/channex/properties/${propertyId}/sync-room-types`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // Update the property's channexMapping with new room types and rate plans
        if (response.data.roomTypes) {
          setChannexMapping(prev => ({
            ...prev,
            roomTypes: response.data.roomTypes
          }));

          // For apartment-style properties, auto-select room type
          if (response.data.roomTypes.length === 1) {
            const singleRoomType = response.data.roomTypes[0];

            // Always set the room type for single-room properties
            setBulkRateForm(prev => ({
              ...prev,
              roomTypeId: singleRoomType.id,
              // Auto-select rate plan if there's only one
              ratePlanId: singleRoomType.ratePlans?.length === 1 ? singleRoomType.ratePlans[0].id : ''
            }));

            setAvailabilityForm(prev => ({
              ...prev,
              roomTypeId: singleRoomType.id
            }));

          }
        }

        // Refresh property data to get updated channexMapping
        const updatedPropertyData = await secureApiService.getProperty(propertyId);
        setProperty(updatedPropertyData);
        if (updatedPropertyData?.channexMapping) {
          setChannexMapping(updatedPropertyData.channexMapping);
        }

        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to sync room types');
      }
    } catch (error) {
      console.error('❌ Error syncing room types:', error);
      setRatePlansError(error.message || 'Failed to sync room types and rate plans');
      throw error;
    } finally {
      setRatePlansSyncing(false);
    }
  };

  // Function to fetch property rates from RateMaster
  const fetchPropertyRates = async () => {
    try {
      setRatesLoading(true);
      setRatesError(null);

      const ratesData = await secureApiService.getPropertyRates(propertyId);

      if (ratesData.success) {
        setRates(ratesData.data);

        // Initialize basePrices from fetched rate data
        const newBasePrices = {};

        // First, try to get prices from RateMaster
        Object.values(ratesData.data).forEach(rate => {
          if (rate.roomTypeId && rate.ratePlanId) {
            const key = `${rate.roomTypeId}_${rate.ratePlanId}`;
            newBasePrices[key] = rate.baseRate || 400; // Use baseRate from RateMaster
          }
        });

        // If no RateMaster data, initialize from property room types
        const roomTypesWithPlans = getRoomTypesWithRatePlans({ ...property, channexMapping });
        if (Object.keys(newBasePrices).length === 0 && roomTypesWithPlans.length > 0) {
          roomTypesWithPlans.forEach(room => {
            room.ratePlans?.forEach(ratePlan => {
              const key = `${room.id}_${ratePlan.id}`;
              newBasePrices[key] = 400; // Default price
            });
          });
        }

        // Update basePrices if we have any data
        if (Object.keys(newBasePrices).length > 0) {
          setBasePrices(newBasePrices);
        }
      } else {
        setRatesError('Failed to fetch rates');
      }
    } catch (error) {
      console.error('❌ Error fetching rates:', error);
      setRatesError(error.message || 'Failed to fetch rates');
      setRates({}); // Set empty rates on error
    } finally {
      setRatesLoading(false);
    }
  };

  // Function to fetch reviews for this property
  const fetchReviews = async () => {
    if (!property?._id) return;

    try {
      setReviewsLoading(true);
      setReviewsError(null);

      // Build query parameters
      const params = {
        propertyId: property._id,
        page: 1,
        limit: 100
      };

      if (reviewFilters.status !== 'all') {
        params.status = reviewFilters.status;
      }

      if (reviewFilters.rating) {
        params.rating = reviewFilters.rating;
      }

      // Fetch reviews and stats in parallel
      const [reviewsResponse, statsResponse] = await Promise.all([
        secureApi.get('/api/v1/reviews', { params }),
        secureApi.get(`/api/v1/reviews/property/${property._id}/stats`)
      ]);

      if (reviewsResponse.data.success) {
        setReviews(reviewsResponse.data.reviews || []);
      }

      if (statsResponse.data.success) {
        setReviewStats(statsResponse.data.stats);
      }
    } catch (error) {
      console.error('❌ Error fetching reviews:', error);
      setReviewsError(error.response?.data?.error || 'فشل في تحميل التقييمات');
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  // Fetch stored OTA reviews (Airbnb / Booking.com) for this unit — read-only
  const fetchOtaReviews = async () => {
    if (!property?._id) return;

    try {
      setOtaReviewsLoading(true);
      setOtaReviewsError(null);

      const response = await secureApi.get('/api/v1/ota-reviews', {
        params: { propertyId: property._id, limit: 100 }
      });

      if (response.data.success) {
        setOtaReviews(response.data.reviews || []);
        setOtaTotal(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('❌ Error fetching OTA reviews:', error);
      setOtaReviewsError(error.response?.data?.error || 'فشل في تحميل تقييمات المنصات');
      setOtaReviews([]);
    } finally {
      setOtaReviewsLoading(false);
    }
  };

  // Optimistic: flip the card now, roll back if the server refuses. Public page follows within ~2 min (cache).
  const toggleReviewOnWebsite = async (review) => {
    const hidden = !review.hiddenFromWebsite;
    setOtaVisibilityError(null);
    const setHidden = (value) => setOtaReviews((prev) => prev.map((r) => (
      r._id === review._id ? { ...r, hiddenFromWebsite: value } : r
    )));
    setHidden(hidden);
    try {
      await secureApi.patch(`/api/v1/ota-reviews/${review._id}/website-visibility`, { hidden });
    } catch (error) {
      console.error('❌ Error updating review website visibility:', error);
      setHidden(!hidden);
      setOtaVisibilityError(error.response?.data?.error || 'تعذّر تحديث ظهور التقييم');
    }
  };

  // Approve review
  const approveReview = async (reviewId) => {
    if (!window.confirm('هل تريد الموافقة على هذا التقييم؟')) return;

    try {
      setReviewActionLoading(true);
      const response = await secureApi.patch(`/api/v1/reviews/${reviewId}/approve`);

      if (response.data.success) {
        alert('✅ تم قبول التقييم بنجاح');
        await fetchReviews(); // Refresh reviews
      }
    } catch (error) {
      console.error('❌ Error approving review:', error);
      alert('فشل في قبول التقييم');
    } finally {
      setReviewActionLoading(false);
    }
  };

  // Reject review
  const rejectReview = async (reviewId) => {
    const reason = window.prompt('يرجى تحديد سبب الرفض:');
    if (!reason) return;

    try {
      setReviewActionLoading(true);
      const response = await secureApi.patch(`/api/v1/reviews/${reviewId}/reject`, { reason });

      if (response.data.success) {
        alert('✅ تم رفض التقييم');
        await fetchReviews();
      }
    } catch (error) {
      console.error('❌ Error rejecting review:', error);
      alert('فشل في رفض التقييم');
    } finally {
      setReviewActionLoading(false);
    }
  };

  // Delete review
  const deleteReview = async (reviewId) => {
    if (!window.confirm('هل تريد حذف هذا التقييم نهائياً؟')) return;

    try {
      setReviewActionLoading(true);
      const response = await secureApi.delete(`/api/v1/reviews/${reviewId}`);

      if (response.data.success) {
        alert('✅ تم حذف التقييم');
        await fetchReviews();
      }
    } catch (error) {
      console.error('❌ Error deleting review:', error);
      alert('فشل في حذف التقييم');
    } finally {
      setReviewActionLoading(false);
    }
  };

  // Submit response to review
  const submitReviewResponse = async () => {
    if (!selectedReview || !reviewResponse.trim()) return;

    try {
      setReviewActionLoading(true);
      const response = await secureApi.post(`/api/v1/reviews/${selectedReview._id}/respond`, {
        response: reviewResponse,
        language: 'ar'
      });

      if (response.data.success) {
        alert('✅ تم إضافة الرد بنجاح');
        setShowReviewResponseModal(false);
        setReviewResponse('');
        setSelectedReview(null);
        await fetchReviews();
      }
    } catch (error) {
      console.error('❌ Error adding response:', error);
      alert(error.response?.data?.error || 'فشل في إضافة الرد');
    } finally {
      setReviewActionLoading(false);
    }
  };

  // Helper function to convert bookings to calendar events
  const convertBookingsToEvents = (bookings) => {
    if (!bookings || !Array.isArray(bookings)) {
      return [];
    }

    const events = bookings.map(booking => {
      // Handle different date field names
      // Priority: MongoDB fields first (checkIn/checkOut), then API variations
      const startDate = booking.checkIn || booking.checkInDate || booking.startDate;
      const endDate = booking.checkOut || booking.checkOutDate || booking.endDate;

      if (!startDate || !endDate) {
        console.warn('⚠️ Booking missing dates:', booking);
        return null;
      }

      // Convert to date-only strings for FullCalendar (timezone-agnostic, all-day events)
      // This ensures checkout date is visually excluded (FullCalendar treats end as exclusive)
      const startDateStr = new Date(startDate).toISOString().split('T')[0]; // "2025-11-04"
      const endDateStr = new Date(endDate).toISOString().split('T')[0];     // "2025-11-05"

      const event = {
        id: booking._id,
        title: booking.guestName || 'حجز',
        start: startDateStr,
        end: endDateStr,
        allDay: true, // Explicitly mark as all-day event
        backgroundColor: getPlatformColor(booking.sourcePlatform),
        borderColor: getPlatformColor(booking.sourcePlatform),
        extendedProps: {
          platform: booking.sourcePlatform,
          guestName: booking.guestName,
          bookingId: booking._id
        }
      };

      return event;
    }).filter(Boolean); // Remove null entries

    return events;
  };

  // Helper function to get day data for modal (only called on click)
  const getDayData = (dateStr, dateObj) => {
    // Use dateStr directly from FullCalendar (already in YYYY-MM-DD format, Riyadh timezone)
    // Don't use Date object methods as they use browser timezone

    // Get bookings for this date - check both calendarBookings and activeBookingsForCalendar
    // Note: activeBookingsForCalendar excludes cancelled bookings
    const allBookings = calendarBookings.length > 0 ? calendarBookings : activeBookingsForCalendar;

    const dayBookings = allBookings.filter(booking => {
      // Priority: MongoDB fields first (checkIn/checkOut), then API variations
      const checkInDate = moment(booking.checkIn || booking.checkInDate || booking.startDate);
      const checkOutDate = moment(booking.checkOut || booking.checkOutDate || booking.endDate);

      // Format dates consistently in Riyadh timezone context
      const checkIn = checkInDate.format('YYYY-MM-DD');
      const checkOut = checkOutDate.format('YYYY-MM-DD');

      return dateStr >= checkIn && dateStr < checkOut;
    });

    // Get availability/restrictions from calendarData if available
    // calendarData structure: { "2025-10-17": { "roomId_ratePlanId": { rate, minStay, ... } } }
    const dayData = calendarData[dateStr] || {};

    // Get the first room's data (for single property view, we show the first room/rate plan)
    const roomKeys = Object.keys(dayData);
    const firstRoomKey = roomKeys[0];
    const dayRestrictions = firstRoomKey ? dayData[firstRoomKey] : {};

    const availability = dayRestrictions.availability || 0;
    const booked = dayRestrictions.booked || 0;
    const blocked = dayRestrictions.blocked || 0;
    const total = dayRestrictions.total || 0;
    const stopSell = dayRestrictions.restrictions?.stopSell || false;

    // One derivation of this day's state, shared with the month grid and the legend.
    // The booleans below are derived FROM it rather than computed alongside it, so a
    // consumer cannot reach a different answer than the status line shows — that
    // disagreement is exactly what made a held date read as «متاح للحجز».
    const state = resolveAvailabilityState({
      available: availability,
      booked,
      blocked,
      total,
      stopSell,
      bookingCount: dayBookings.length,
      // No availability row for this date (outside the fetched window, or never
      // seeded) must not be reported as a hold.
      hasData: roomKeys.length > 0
    });

    return {
      date: dateStr, // Use timezone-agnostic string (no conversion needed - date-only, all properties in Saudi)
      dateStr: dateStr,
      bookings: dayBookings,
      state,
      // Sellable per the backend's own rule (available > 0 && !stopSell), which
      // PARTIAL also satisfies — some units remain even though others are spoken for.
      available: state === AVAILABILITY_STATE.AVAILABLE || state === AVAILABILITY_STATE.PARTIAL,
      booked: state === AVAILABILITY_STATE.BOOKED,
      blocked: state === AVAILABILITY_STATE.BLOCKED,
      total,
      rate: dayRestrictions.rate || property?.basePrice || 0,
      minStay: dayRestrictions.minStay || property?.minNights || 1,
      maxStay: dayRestrictions.restrictions?.maxStay || property?.maxNights || 30,
      stopSell,
      restrictions: dayRestrictions.restrictions || {}
    };
  };

  // Handle day cell click to open modal
  const handleDayClick = (dateStr, dateObj) => {
    const dayData = getDayData(dateStr, dateObj);
    setSelectedDayData(dayData);
    setShowDayModal(true);
  };

  // Function to fetch calendar data from internal availability API
  const fetchCalendarData = async (startDate = calendarCurrentDate) => {
    try {
      setCalendarLoading(true);
      setCalendarError(false);

      // Calculate end date (365 days from start for full year view)
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 365);
      
      const response = await axios.get(`/api/v1/inventory/calendar/${property?._id || propertyId}`, {
        params: {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = response.data;

      // Store bookings separately (excluding cancelled bookings)
      if (data.data?.bookings) {
        const activeBookings = data.data.bookings.filter(booking => booking.status !== 'cancelled');
        setCalendarBookings(activeBookings);
      }

      // Transform data into a format easy to display
      // Structure: { date: { roomTypeId_ratePlanId: { availability, minStay, rates, restrictions } } }
      const transformedData = {};

      if (data.data?.calendar) {
        data.data.calendar.forEach(item => {
          const dateKey = item.date.split('T')[0];
          if (!transformedData[dateKey]) {
            transformedData[dateKey] = {};
          }
          
          // Create a unique key combining roomTypeId and ratePlanId for hotel-style properties
          const roomKey = item.roomTypeId && item.ratePlanId 
            ? `${item.roomTypeId}_${item.ratePlanId}`
            : item.roomTypeId || 'default';
            
          transformedData[dateKey][roomKey] = {
            availability: item.available || 0,
            booked: item.booked || 0,
            blocked: item.blocked || 0,
            // Needed to tell "all units held" from "some units held" on multi-unit
            // rooms. Optional: absent when the frontend runs ahead of the backend,
            // in which case the resolver falls back to single-unit behaviour.
            total: item.total || 0,
            minStay: item.minStay || item.restrictions?.minStayArrival || 1,
            rate: item.rate || item.rates?.standard || 0,  // Handle both item.rate and item.rates.standard
            restrictions: item.restrictions || {},
            syncStatus: item.syncStatus || 'unknown',
            lastSync: item.lastSync
          };
          
          // If roomTypeId exists, also store availability at room type level
          // This ensures availability displays correctly for hotels
          if (item.roomTypeId) {
            const roomTypeKey = `roomType_${item.roomTypeId}`;
            if (!transformedData[dateKey][roomTypeKey]) {
              transformedData[dateKey][roomTypeKey] = {
                availability: item.available || 0
              };
            } else {
              // Use the maximum availability across all rate plans for the same room type
              transformedData[dateKey][roomTypeKey].availability = Math.max(
                transformedData[dateKey][roomTypeKey].availability,
                item.available || 0
              );
            }
          }
        });
      }

      setCalendarData(transformedData);

    } catch (error) {
      console.error('❌ Error fetching calendar data:', error);
      // Fall back to empty data — and say so, or the empty month reads as available
      setCalendarData({});
      setCalendarError(true);
    } finally {
      setCalendarLoading(false);
    }
  };

  // Import availability and rates from Channex (NEW for onboarding)
  // eslint-disable-next-line no-unused-vars
  const handleImportFromChannex = async () => {
    try {
      setSyncStatus('loading');
      setSyncMessage('جاري استيراد البيانات من Channex...');

      const response = await axios.post(
        `/api/v1/channex/properties/${propertyId}/import-availability`,
        { days: 365 }, // Import 1 year of data
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSyncStatus('success');
        setSyncMessage(`✅ تم استيراد ${response.data.totalRecords} سجل من Channex بنجاح`);

        // Refresh calendar data to show imported rates
        if (activeTab === 'calendar') {
          await fetchCalendarData();
        }
      }

      // Reset status after 3 seconds
      setTimeout(() => {
        setSyncStatus('idle');
        setSyncMessage('');
      }, 3000);

    } catch (error) {
      console.error('Import from Channex error:', error);
      setSyncStatus('error');
      setSyncMessage(`❌ فشل الاستيراد: ${error.response?.data?.message || error.message}`);

      // Reset status after 5 seconds
      setTimeout(() => {
        setSyncStatus('idle');
        setSyncMessage('');
      }, 5000);
    }
  };

  // Handle booking sync
  const handleSyncBookings = async () => {
    try {
      setBookingSyncStatus('syncing');
      setBookingSyncMessage('');

      // Import the raw axios instance for this custom endpoint
      const { secureApi } = await import('../services/secureApi');
      // Use MongoDB property ID, not Channex property ID, with correct API version
      const response = await secureApi.post(`/api/v1/channex/bookings/sync/${property._id || propertyId}`);
      
      if (response.data.success) {
        setBookingSyncStatus('success');
        setBookingSyncMessage('تمت مزامنة الحجوزات بنجاح');

        // Refresh bookings list after a short delay
        setTimeout(async () => {
          if (property?._id) {
            // Fetch updated bookings
            try {
              const propertyData = await secureApiService.getProperty(propertyId);
              setProperty(propertyData);
              // Refetch bookings from React Query cache
              refetchBookings();
            } catch (err) {
              console.error('Error refreshing property after sync:', err);
            }
          }
        }, 3000);
        
        // Reset status after 5 seconds
        setTimeout(() => {
          setBookingSyncStatus('idle');
          setBookingSyncMessage('');
        }, 5000);
      } else {
        throw new Error(response.data.message || 'فشلت مزامنة الحجوزات');
      }
    } catch (error) {
      console.error('❌ Error syncing bookings:', error);
      setBookingSyncStatus('error');
      setBookingSyncMessage(error.response?.data?.message || 'حدث خطأ أثناء مزامنة الحجوزات');
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setBookingSyncStatus('idle');
        setBookingSyncMessage('');
      }, 5000);
    }
  };

  // Fetch calendar data when calendar tab is active
  useEffect(() => {
    if (activeTab === 'calendar' && property?._id) {
      fetchCalendarData(calendarCurrentDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, calendarCurrentDate, property?._id]);

  // Fetch reviews when reviews tab is active
  useEffect(() => {
    if (activeTab === 'reviews' && reviewSubTab === 'direct' && property?._id) {
      fetchReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reviewSubTab, reviewFilters, property?._id]);

  // Fetch OTA reviews when the external-platforms sub-tab is opened (lazy)
  useEffect(() => {
    if (activeTab === 'reviews' && reviewSubTab === 'ota' && property?._id) {
      fetchOtaReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reviewSubTab, property?._id]);

  // Door-plaque link — admin / ops-manager only, and only ever from its own endpoint.
  // The server enforces the same two roles; this check exists so the panel isn't requested at all,
  // not as the permission itself. Lazy: nothing is fetched until an operator opens the section.
  useEffect(() => {
    if (!plaqueOpen || plaqueLink || plaqueError) return;
    if (!property?._id) return;
    if (user?.role !== 'admin' && user?.role !== 'ops-manager') return;

    let cancelled = false;
    secureApi.get(`/api/v1/properties/${property._id}/plaque-link`)
      .then((res) => {
        if (cancelled) return;
        setPlaqueLink(res.data);
        setPlaqueError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setPlaqueLink(null);
        // A unit created before the feature has no token yet — a distinct, actionable state, not
        // a failure. Anything else is a genuine error.
        setPlaqueError(err?.response?.data?.code === 'NO_CHECKOUT_TOKEN' ? 'no-token' : 'failed');
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plaqueOpen, property?._id, user?.role]);

  // 🔴 DROP THE LINK THE MOMENT THE UNIT CHANGES. React Router reuses this component when only the
  // `:propertyId` param changes, so without this an operator who opened the panel on one unit and
  // navigated to another would be shown the FIRST unit's URL under the second unit's name — the
  // wrong-plaque-on-wrong-door failure, produced on screen instead of at the door. The fetch effect
  // deliberately does not refetch while a link is held, which is exactly why the reset must exist.
  useEffect(() => {
    setPlaqueOpen(false);
    setPlaqueLink(null);
    setPlaqueError(null);
    setPlaqueCopied(false);
    setPlaqueQrError(false);
  }, [propertyId]);

  // Booking-links panel: close on click-outside or Escape, and reset when the unit changes so one
  // unit's links can never sit open above another unit's name.
  useEffect(() => {
    if (!bookingLinksOpen) return;

    const handleOutside = (event) => {
      if (bookingLinksRef.current && !bookingLinksRef.current.contains(event.target)) {
        setBookingLinksOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setBookingLinksOpen(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [bookingLinksOpen]);

  useEffect(() => {
    setBookingLinksOpen(false);
    setCopiedLinkKey(null);
  }, [propertyId]);

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the dropdown container
      if (showViewDropdown && !event.target.closest('.dropdown')) {
        setShowViewDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showViewDropdown]);

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      try {
        // Fetch property details using secure API
        const propertyData = await secureApiService.getProperty(propertyId);
        setProperty(propertyData);
        
        // Initialize Channex mapping state
        setChannexMapping(propertyData.channexMapping || {
          propertyId: '',
          roomTypes: [],
          status: 'unmapped'
        });
        
        // Fetch channel information if property is mapped to Channex
        if (propertyData.channexMapping?.propertyId) {
          try {
            const channelData = await secureApiService.getChannels(propertyId);
            setChannelInfo(channelData.data);
            setIsAirbnbProperty(channelData.data.hasAirbnb);
            setChannelConstraints(channelData.data.constraints);
          } catch (channelErr) {
            console.error('❌ Error fetching channel data:', channelErr);
            // Continue without channel data
          }
        }
        
        // Fetch rates from RateMaster
        await fetchPropertyRates();

        // Note: Bookings are now fetched via useBookings React Query hook
        // No need to fetch here - hook handles caching and auto-refetching

        setLoading(false);
      } catch (err) {
        console.error('Error fetching property details:', err);
        if (err.response?.status === 403) {
          setError('ليس لديك صلاحية لعرض هذا العقار');
        } else if (err.response?.status === 404) {
          setError('العقار غير موجود');
        } else {
          setError('حدث خطأ أثناء تحميل تفاصيل العقار');
        }
        setLoading(false);
      }
    };

    fetchPropertyDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  // Apply bookings filters whenever filters or bookings change
  useEffect(() => {
    if (!mvpBookings) {
      setFilteredBookings([]);
      return;
    }

    let filtered = [...mvpBookings];

    // Status filter
    if (bookingsFilters.status) {
      filtered = filtered.filter(booking => booking.status === bookingsFilters.status);
    }

    // Platform filter
    if (bookingsFilters.platform) {
      filtered = filtered.filter(booking =>
        matchesPlatformFilter(booking.sourcePlatform || booking.platform, bookingsFilters.platform)
      );
    }

    // Search filter (guest name or confirmation code)
    if (bookingsFilters.search) {
      const searchTerm = bookingsFilters.search.toLowerCase();
      filtered = filtered.filter(booking =>
        booking.guestName?.toLowerCase().includes(searchTerm) ||
        booking.summary?.toLowerCase().includes(searchTerm) ||
        booking.confirmationCode?.toLowerCase().includes(searchTerm)
      );
    }

    // Date range filter - shared with BookingsList
    filtered = filterBookingsByDateRange(filtered, bookingsFilters.dateRange, {
      from: bookingsFilters.dateFrom,
      to: bookingsFilters.dateTo
    });

    setFilteredBookings(filtered);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingsFilters, mvpBookings?.length]);

  // Reset bookings filters - reset to default (upcoming)
  const resetBookingsFilters = () => {
    setBookingsFilters({
      status: 'upcoming',
      platform: '',
      dateRange: DATE_RANGE_ALL,
      dateFrom: '',
      dateTo: '',
      search: ''
    });
  };

  // Handle property refresh
  const handleRefreshProperty = async () => {
    try {
      await secureApiService.syncProperty(propertyId);
      // Reload property data
      const propertyData = await secureApiService.getProperty(propertyId);
      setProperty(propertyData);

      alert('تم تحديث بيانات العقار بنجاح');
    } catch (err) {
      console.error('Error refreshing property:', err);
      alert('حدث خطأ أثناء تحديث بيانات العقار');
    }
  };

  // Save Channex mapping configuration - Currently unused
  /*
  const saveChannexMapping = async () => {
    try {
      setSyncStatus('syncing');
      setSyncMessage('جاري حفظ الإعدادات...');

      const response = await axios.put(`/api/properties/${propertyId}`, {
        channexMapping: channexMapping
      });

      if (response.data) {
        setProperty(response.data);
        setSyncStatus('success');
        setSyncMessage('تم حفظ الإعدادات بنجاح');
        
        // Reset status after 3 seconds
        setTimeout(() => {
          setSyncStatus('idle');
          setSyncMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to save Channex mapping:', error);
      setSyncStatus('error');
      setSyncMessage(error.response?.data?.message || 'فشل في حفظ الإعدادات');
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setSyncStatus('idle');
        setSyncMessage('');
      }, 5000);
    }
  };
  */

  // Sync availability to Channex
  const syncAvailability = async () => {
    try {
      setSyncStatus('syncing');
      setSyncMessage('جاري مزامنة التوفر...');

      const response = await axios.post(`/api/v1/channex/${propertyId}/sync-availability`, {
        forceSync: false
      });

      if (response.data.success) {
        setSyncStatus('success');
        setSyncMessage(`تمت مزامنة ${response.data.data.recordsSynced} سجل بنجاح`);
      } else {
        throw new Error(response.data.message || 'فشلت المزامنة');
      }
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setSyncStatus('idle');
        setSyncMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Failed to sync availability:', error);
      setSyncStatus('error');
      setSyncMessage(error.response?.data?.message || 'فشلت مزامنة التوفر');
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setSyncStatus('idle');
        setSyncMessage('');
      }, 5000);
    }
  };

  // Test 1 Certification Functions

  // Initialize basePrices when room types change (for new properties without rates)
  useEffect(() => {
    const roomTypesWithPlans = getRoomTypesWithRatePlans({ ...property, channexMapping });
    if (roomTypesWithPlans.length > 0 && Object.keys(basePrices).length === 0) {
      const initialPrices = {};
      roomTypesWithPlans.forEach(room => {
        room.ratePlans?.forEach(ratePlan => {
          const key = `${room.id}_${ratePlan.id}`;
          // Check if we already have a price from rates
          if (!basePrices[key]) {
            initialPrices[key] = 400; // Default price
          }
        });
      });
      if (Object.keys(initialPrices).length > 0) {
        setBasePrices(initialPrices);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channexMapping]);

  // Auto-fetch room types and rate plans when modal opens
  useEffect(() => {
    const fetchRatePlansIfNeeded = async () => {
      // Only fetch if modal is open and we don't have room types
      const roomTypes = getRoomTypes({ ...property, channexMapping });
      if (showBulkRateModal && roomTypes.length === 0) {
        try {
          await syncRoomTypesAndRatePlans();
        } catch (error) {
          console.error('Failed to sync room types:', error);
          // Show error to user but don't close modal
        }
      } else if (showBulkRateModal && roomTypes.length >= 1) {
        // Auto-select for apartment-style properties (single room type)
        if (roomTypes.length === 1) {
          const singleRoomType = getRoomTypesWithRatePlans({ ...property, channexMapping })[0];

          // Always auto-set the room type for single room properties
          if (!bulkRateForm.roomTypeId) {
            setBulkRateForm(prev => ({
              ...prev,
              roomTypeId: singleRoomType.id,
              // Auto-select rate plan if there's only one
              ratePlanId: singleRoomType.ratePlans?.length === 1 ? singleRoomType.ratePlans[0].id : prev.ratePlanId
            }));
          }

          // Auto-select for availability tab
          if (!availabilityForm.roomTypeId) {
            setAvailabilityForm(prev => ({
              ...prev,
              roomTypeId: singleRoomType.id
            }));
          }

        }
      }
    };

    if (showBulkRateModal) {
      fetchRatePlansIfNeeded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBulkRateModal, channexMapping]);

  // Helper function to update individual base price
  const updateBasePrice = (roomId, ratePlanId, value) => {
    setBasePrices(prev => ({
      ...prev,
      [`${roomId}_${ratePlanId}`]: parseInt(value) || 400
    }));
  };

  // NEW: Unified function to generate and sync 500 days of data
  const generateAndSyncFullData = async () => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 1); // Start tomorrow

    const fullData = [];

    // Get room types and rate plans from property
    const roomTypes = getRoomTypesWithRatePlans({ ...property, channexMapping });

    if (roomTypes.length === 0) {
      setSyncMessage('❌ No room types found. Please create room types first in the Channex Integration section.');
      setSyncStatus('error');
      return;
    }

    // Check if property is apartment or hotel
    const propertyIsApartment = ['شقة', 'فيلا', 'غرفة'].includes(property?.type);

    // Generate 500 days of unified data
    for (let i = 0; i < 500; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];

      const isWeekend = date.getDay() === 5 || date.getDay() === 6; // Friday/Saturday
      const isHighSeason = date.getMonth() >= 10 || date.getMonth() <= 2; // Nov-Feb

      // Generate records for each room type and rate plan
      roomTypes.forEach(roomType => {
        // For apartments: 0 or 1 availability, for hotels: 3-5 rooms
        const roomInventory = propertyIsApartment
          ? Math.round(Math.random()) // 0 or 1 for apartments
          : Math.floor(Math.random() * 3) + 3; // 3-5 rooms for hotels

        roomType.ratePlans?.forEach(ratePlan => {
          // Get specific base price for this room/rateplan combo
          const key = `${roomType.id}_${ratePlan.id}`;
          const specificBasePrice = basePrices[key] || basePrice || 400;

          // Calculate price with weekend increase (dynamic based on user input)
          const weekendMultiplier = 1 + (Number(weekendIncrease) ?? 0) / 100; // Proper zero handling with ?? operator
          const dayPrice = isWeekend ? specificBasePrice * weekendMultiplier : specificBasePrice;
          const finalPrice = parseFloat((dayPrice + Math.random() * 50).toFixed(2)); // Small random variation

          fullData.push({
            date: dateStr,
            roomTypeId: roomType.id,
            ratePlanId: ratePlan.id,
            // CRITICAL: Same inventory for all rate plans of the same room type
            inventory: {
              total: roomInventory,
              available: roomInventory
            },
            rates: {
              standard: finalPrice,
              currency: 'SAR'
            },
            restrictions: {
              minStayArrival: 1, // Same minimum stay for all days (no weekend restriction)
              minStayThrough: 1,
              maxStay: 30, // Simplified
              closedToArrival: false, // No automatic blocking
              closedToDeparture: false, // Simplified
              stopSell: isHighSeason && Math.random() < 0.05 // 5% in high season
            }
          });
        });
      });
    }

    // Now sync the unified data
    try {
      setSyncStatus('syncing');
      setSyncMessage(`جاري إنشاء ومزامنة ${fullData.length} سجل...`);
      setSyncProgress(25);

      const response = await axios.post('/api/v1/channex/full-sync', {
        propertyId: propertyId,
        data: fullData,
        testType: 'full_sync'
      });

      setSyncProgress(100);

      setSyncMessage(`✅ تم إنشاء ومزامنة ${fullData.length} سجل بنجاح`);
      setSyncStatus('success');

      // Update property state with initialSyncCompleted flag
      if (response.data.results?.initialSyncCompleted) {
        setProperty(prev => ({
          ...prev,
          channexMapping: {
            ...prev.channexMapping,
            initialSyncCompleted: true,
            initialSyncCompletedAt: response.data.results.lastSyncedAt,
            lastSyncedAt: response.data.results.lastSyncedAt
          }
        }));
      }

      // Store the data for reference
      setSyncData({ fullData });

      setTimeout(() => {
        setSyncStatus('idle');
        setSyncMessage('');
      }, 3000);

    } catch (error) {
      console.error('❌ Full sync failed:', error);
      setSyncMessage(`❌ فشلت المزامنة: ${error.message}`);
      setSyncStatus('error');
    }
  };

  // Copy task IDs to clipboard for certification
  const copyTaskIds = () => {
    if (!syncTaskIds?.availability || !syncTaskIds?.restrictions) {
      alert('يرجى إكمال مزامنة البيانات أولاً');
      return;
    }
    
    const text = `معرفات المزامنة:\nالتوفر: ${syncTaskIds.availability}\nالقيود: ${syncTaskIds.restrictions}`;
    navigator.clipboard.writeText(text);
    alert('تم نسخ معرفات المهام إلى الحافظة!');
  };
  
  // Note: Individual rate updates removed - use bulk modal for all price changes
  
  // OLD addToBulkRatesList function removed - was using checkbox logic
  // Now using addBulkRateToList() function which uses direct values

  const removeFromBulkRatesList = (id) => {
    setBulkRatesList(bulkRatesList.filter(item => item.id !== id));
  };

  // Add bulk rate to list
  const addBulkRateToList = () => {
    // Validation
    if (!bulkRateForm.date) {
      alert('يرجى اختيار التاريخ');
      return;
    }

    if (bulkRateForm.updateType === 'range' && !bulkRateForm.dateEnd) {
      alert('يرجى اختيار تاريخ النهاية');
      return;
    }

    // Validate at least one day is selected for range updates
    if (bulkRateForm.updateType === 'range') {
      const selectedDays = bulkRateForm.selectedDays || [];
      if (selectedDays.length === 0) {
        alert('يرجى اختيار يوم واحد على الأقل من أيام الأسبوع');
        return;
      }
    }

    // Check if at least one field has a value or stop sell action is set
    if (!bulkRateForm.rate && !bulkRateForm.minStayArrival && !bulkRateForm.maxStay && bulkRateForm.stopSellAction === 'none') {
      alert('يرجى إدخال قيمة واحدة على الأقل (سعر أو قيود أو حالة بيع)');
      return;
    }

    // Validate price if provided
    if (bulkRateForm.rate) {
      const price = parseFloat(bulkRateForm.rate);
      if (isNaN(price) || price <= 0) {
        alert('يرجى إدخال سعر صحيح');
        return;
      }
    }

    // Validate min stay if provided
    if (bulkRateForm.minStayArrival) {
      const minStay = parseInt(bulkRateForm.minStayArrival);
      if (isNaN(minStay) || minStay < 1) {
        alert('يرجى إدخال قيمة صحيحة للحد الأدنى للإقامة');
        return;
      }
    }

    // Validate max stay if provided
    if (bulkRateForm.maxStay) {
      const maxStay = parseInt(bulkRateForm.maxStay);
      if (isNaN(maxStay) || maxStay < 1) {
        alert('يرجى إدخال قيمة صحيحة للحد الأقصى للإقامة');
        return;
      }
    }

    // Validate rate plan selection (REQUIRED for rates/restrictions)
    if (!bulkRateForm.ratePlanId) {
      alert('يرجى اختيار خطة السعر');
      return;
    }

    // Get effective room type for Channex operations (supports both single-unit and multi-unit)
    // For multi-unit: uses linkedRoomTypeId; for single-unit: uses roomTypes[0].id
    const effectiveRoomMapping = getEffectiveRoomMapping({ ...property, channexMapping });
    const defaultRoomTypeId = effectiveRoomMapping?.roomTypeId;

    // Validate room type exists
    if (!defaultRoomTypeId) {
      alert('❌ لا توجد أنواع غرف محددة. يرجى إعداد نوع غرفة واحد على الأقل للعقار.');
      console.error('No room types found for property');
      return;
    }

    // Get selected rate plan info from the effective room mapping
    const selectedRatePlan = effectiveRoomMapping?.ratePlans?.find(rp => rp.id === bulkRateForm.ratePlanId);

    // Validate rate plan exists
    if (!selectedRatePlan) {
      alert('❌ خطة السعر المحددة غير موجودة. يرجى تحديث الصفحة والمحاولة مرة أخرى.');
      console.error('Selected rate plan not found:', bulkRateForm.ratePlanId);
      return;
    }

    const roomTypeName = property?.type || 'شقة';
    const ratePlanName = selectedRatePlan?.name || 'Standard';

    // Handle different update types
    if (bulkRateForm.updateType === 'range') {
      // Generate entries for date range with day-of-week filtering
      // Add T12:00:00 to avoid timezone issues (ensures date is parsed at noon)
      const startDate = new Date(bulkRateForm.date + 'T12:00:00');
      const endDate = new Date(bulkRateForm.dateEnd + 'T12:00:00');
      const selectedDays = bulkRateForm.selectedDays || [0, 1, 2, 3, 4, 5, 6]; // Default to all days

      // Store individual entries for backend submission
      const individualEntries = [];
      for (let currentDate = new Date(startDate); currentDate <= endDate; currentDate.setDate(currentDate.getDate() + 1)) {
        // Filter by selected days of week
        const dayOfWeek = currentDate.getDay(); // 0=Sunday, 6=Saturday
        if (!selectedDays.includes(dayOfWeek)) {
          continue; // Skip this date if not in selected days
        }

        individualEntries.push({
          roomTypeId: defaultRoomTypeId, // Use actual room type ID
          ratePlanId: bulkRateForm.ratePlanId, // Use selected rate plan from dropdown
          roomTypeName: roomTypeName,
          ratePlanName: ratePlanName,
          date: new Date(currentDate).toISOString().split('T')[0],
          // Include fields that have values (simplified - no checkboxes)
          ...(bulkRateForm.rate && { rate: parseFloat(bulkRateForm.rate) || 0 }),
          ...(bulkRateForm.minStayArrival && { minStayArrival: parseInt(bulkRateForm.minStayArrival) || 1 }),
          ...(bulkRateForm.maxStay && { maxStay: parseInt(bulkRateForm.maxStay) || 30 }),
          // Map stopSellAction: 'none' → skip, 'allow' → false, 'block' → true
          ...(bulkRateForm.stopSellAction === 'allow' && { stopSell: false }),
          ...(bulkRateForm.stopSellAction === 'block' && { stopSell: true })
        });
      }

      const dayCount = individualEntries.length; // Actual count after filtering

      // Create a consolidated entry for display
      const rangeEntry = {
        id: Date.now(),
        roomTypeId: defaultRoomTypeId,
        ratePlanId: bulkRateForm.ratePlanId,
        roomTypeName: roomTypeName,
        ratePlanName: ratePlanName,
        dateStart: bulkRateForm.date,
        dateEnd: bulkRateForm.dateEnd,
        dayCount: dayCount,
        isDateRange: true,
        expandedEntries: individualEntries,
        selectedDays: bulkRateForm.selectedDays, // Store for display
        // Include fields for display (simplified)
        ...(bulkRateForm.rate && { rate: parseFloat(bulkRateForm.rate) || 0 }),
        ...(bulkRateForm.minStayArrival && { minStayArrival: parseInt(bulkRateForm.minStayArrival) || 1 }),
        ...(bulkRateForm.maxStay && { maxStay: parseInt(bulkRateForm.maxStay) || 30 }),
        // Map stopSellAction: 'none' → skip, 'allow' → false, 'block' → true
        ...(bulkRateForm.stopSellAction === 'allow' && { stopSell: false }),
        ...(bulkRateForm.stopSellAction === 'block' && { stopSell: true })
      };

      setBulkRatesList([...bulkRatesList, rangeEntry]);
    } else if (bulkRateForm.updateType === 'baseRate') {
      // Base rate update (internal reference)
      const newEntry = {
        id: Date.now(),
        roomTypeId: defaultRoomTypeId,
        ratePlanId: bulkRateForm.ratePlanId,
        roomTypeName: roomTypeName,
        ratePlanName: ratePlanName,
        date: new Date().toISOString().split('T')[0],
        rate: parseFloat(bulkRateForm.rate),
        isBaseRate: true
      };

      setBulkRatesList([...bulkRatesList, newEntry]);
    } else {
      // Single date update
      const newEntry = {
        id: Date.now(),
        roomTypeId: defaultRoomTypeId,
        ratePlanId: bulkRateForm.ratePlanId,
        roomTypeName: roomTypeName,
        ratePlanName: ratePlanName,
        date: bulkRateForm.date,
        // Include fields that have values (simplified - no checkboxes)
        ...(bulkRateForm.rate && { rate: parseFloat(bulkRateForm.rate) || 0 }),
        ...(bulkRateForm.minStayArrival && { minStayArrival: parseInt(bulkRateForm.minStayArrival) || 1 }),
        ...(bulkRateForm.maxStay && { maxStay: parseInt(bulkRateForm.maxStay) || 30 }),
        // Map stopSellAction: 'none' → skip, 'allow' → false, 'block' → true
        ...(bulkRateForm.stopSellAction === 'allow' && { stopSell: false }),
        ...(bulkRateForm.stopSellAction === 'block' && { stopSell: true })
      };

      setBulkRatesList([...bulkRatesList, newEntry]);
    }

    // Reset form but keep update type and selected days
    setBulkRateForm({
      ...bulkRateForm,
      date: '',
      dateEnd: '',
      rate: '',
      minStayArrival: 1,
      minStayThrough: 1,
      maxStay: 0,
      closedToArrival: false,
      closedToDeparture: false,
      stopSellAction: 'none'
    });
  };

  const submitBulkRates = async () => {
    if (bulkRatesList.length === 0) {
      alert('يرجى إضافة تحديثات أولاً');
      return;
    }
    
    try {
      setBulkRateStatus('updating');
      
      // Separate different types of updates
      const baseRateUpdates = bulkRatesList.filter(item => item.isBaseRate);
      const dateSpecificUpdates = bulkRatesList.filter(item => !item.isBaseRate);
      
      // Handle base rate updates locally only
      if (baseRateUpdates.length > 0) {
        for (const baseRate of baseRateUpdates) {
          await axios.put(`/api/v1/rates/property/${propertyId}/rate-plan/${baseRate.ratePlanId}`, {
            baseRate: baseRate.rate,
            currency: 'SAR'
          }, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
        }
        
      }

      // Process date-specific rate/restriction updates
      const allUpdates = [];
      
      // Handle date-specific rate/restriction updates
      if (dateSpecificUpdates.length > 0) {
        // Expand date ranges into individual entries
        const expandedUpdates = [];
        for (const item of dateSpecificUpdates) {
          if (item.isDateRange && item.expandedEntries) {
            expandedUpdates.push(...item.expandedEntries);
          } else {
            expandedUpdates.push(item);
          }
        }
        
        for (const item of expandedUpdates) {
          const update = {
            date: item.date,
            roomTypeId: item.roomTypeId,
            ratePlanId: item.ratePlanId
          };
          
          // Add rate if specified
          if (item.rate !== undefined) {
            update.rates = { standard: item.rate, currency: 'SAR' };
          }
          
          // Add restrictions if specified
          const restrictions = {};
          if (item.minStayArrival !== undefined) restrictions.minStayArrival = item.minStayArrival;
          if (item.minStayThrough !== undefined) restrictions.minStayThrough = item.minStayThrough;
          if (item.maxStay !== undefined) restrictions.maxStay = item.maxStay;
          if (item.closedToArrival !== undefined) restrictions.closedToArrival = item.closedToArrival;
          if (item.closedToDeparture !== undefined) restrictions.closedToDeparture = item.closedToDeparture;
          if (item.stopSell !== undefined) restrictions.stopSell = item.stopSell;

          if (Object.keys(restrictions).length > 0) {
            update.restrictions = restrictions;
          }
          
          allUpdates.push(update);
        }
      }
      
      // Send all updates through unified inventory endpoint (Local-First)
      if (allUpdates.length > 0) {
        const response = await axios.post('/api/v1/inventory/bulk', {
          propertyId: property?._id || propertyId,
          updates: allUpdates,
          timezone: 'Asia/Riyadh',
          modifiedBy: 'manual'
        }, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.data.success) {
          throw new Error(response.data.message || 'فشل تحديث البيانات');
        }
      }
      
      // Success message
      const baseRateMsg = baseRateUpdates.length > 0 ? `\n${baseRateUpdates.length} سعر مرجعي (داخلي)` : '';
      const dateMsg = dateSpecificUpdates.length > 0 ? `\n${dateSpecificUpdates.length} سعر بتاريخ محدد (Channex)` : '';
      
      alert(`✅ تم التحديث بنجاح!${baseRateMsg}${dateMsg}`);
      setBulkRatesList([]);
      setShowBulkRateModal(false);
      setBulkRateStatus('success');
      
      // Refresh displayed rates
      await fetchPropertyRates();
    } catch (error) {
      console.error('❌ Rate update error:', error);
      alert(`❌ فشل تحديث الأسعار: ${error.response?.data?.message || error.message}`);
      setBulkRateStatus('error');
    }
  };
  

  // Add availability to list (Used in Pricing Modal's Availability Tab)
  const addAvailabilityToList = () => {
    // Validation
    if (!availabilityForm.date || availabilityForm.availableRooms === undefined || availabilityForm.availableRooms === '') {
      alert('يرجى تحديد التاريخ وحالة الوحدة');
      return;
    }

    // Validate date range if applicable
    if (availabilityForm.updateType === 'range') {
      if (!availabilityForm.dateEnd) {
        alert('يرجى تحديد تاريخ النهاية للنطاق');
        return;
      }
      if (new Date(availabilityForm.dateEnd) < new Date(availabilityForm.date)) {
        alert('تاريخ النهاية يجب أن يكون بعد تاريخ البداية');
        return;
      }
    }

    // Get effective room type for Channex operations (supports both single-unit and multi-unit)
    // For multi-unit: uses linkedRoomTypeId; for single-unit: uses roomTypes[0].id
    const effectiveRoomMapping = getEffectiveRoomMapping({ ...property, channexMapping });
    const defaultRoomTypeId = effectiveRoomMapping?.roomTypeId;

    // Validate room type exists
    if (!defaultRoomTypeId) {
      alert('❌ لا توجد أنواع غرف محددة. يرجى إعداد نوع غرفة واحد على الأقل للعقار.');
      console.error('No room types found for property');
      return;
    }

    // For apartments, use the property type name
    const roomTypeName = property?.type || 'شقة';

    const newEntries = [];

    if (availabilityForm.updateType === 'range') {
      // Date range - create consolidated display entry
      const startDate = new Date(availabilityForm.date);
      const endDate = new Date(availabilityForm.dateEnd || availabilityForm.date);
      const dayCount = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

      // Create individual entries for API submission
      const individualEntries = [];
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        individualEntries.push({
          id: Date.now() + individualEntries.length,
          roomTypeId: defaultRoomTypeId,
          roomTypeName: roomTypeName,
          date: d.toISOString().split('T')[0],
          availableRooms: availabilityForm.availableRooms,
          totalRooms: availabilityForm.totalRooms || undefined
        });
      }

      // Create consolidated display entry
      newEntries.push({
        id: Date.now(),
        roomTypeId: defaultRoomTypeId,
        roomTypeName: roomTypeName,
        date: availabilityForm.date,
        dateStart: availabilityForm.date,
        dateEnd: availabilityForm.dateEnd || availabilityForm.date,
        dayCount: dayCount,
        availableRooms: availabilityForm.availableRooms || 0,
        isDateRange: true,
        // Store individual entries for API submission
        expandedEntries: individualEntries
      });
    } else {
      // Single date
      newEntries.push({
        id: Date.now(),
        roomTypeId: defaultRoomTypeId,
        roomTypeName: roomTypeName,
        date: availabilityForm.date,
        availableRooms: availabilityForm.availableRooms || 0,
        isDateRange: false
      });
    }

    setAvailabilityList([...availabilityList, ...newEntries]);

    // Reset form
    setAvailabilityForm({
      ...availabilityForm,
      date: '',
      dateEnd: '',
      availableRooms: 1
    });
  };

  // Remove availability from list
  const removeFromAvailabilityList = (id) => {
    setAvailabilityList(availabilityList.filter(item => item.id !== id));
  };

  // Submit availability updates
  const submitAvailability = async () => {
    if (availabilityList.length === 0) {
      alert('يرجى إضافة تحديثات أولاً');
      return;
    }

    try {
      setAvailabilityUpdateStatus('updating');

      // Process availability updates
      const allUpdates = [];

      for (const item of availabilityList) {
        if (item.isDateRange) {
          // Expand date range into individual days
          const startDate = new Date(item.dateStart);
          const endDate = new Date(item.dateEnd);

          for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            const availableCount = parseInt(item.availableRooms) || 0;
            allUpdates.push({
              date: d.toISOString().split('T')[0],
              roomTypeId: item.roomTypeId, // Use the roomTypeId from the item (required)
              inventory: {
                available: availableCount,
                blocked: availableCount === 0 ? 1 : 0  // Set blocked when manually setting to unavailable
              }
              // NOTE: No ratePlanId needed - availability is at room level
            });
          }
        } else {
          // Single date
          const availableCount = parseInt(item.availableRooms) || 0;
          allUpdates.push({
            date: item.date,
            roomTypeId: item.roomTypeId, // Use the roomTypeId from the item (required)
            inventory: {
              available: availableCount,
              blocked: availableCount === 0 ? 1 : 0  // Set blocked when manually setting to unavailable
            }
            // NOTE: No ratePlanId needed - availability is at room level
          });
        }
      }

      // Send updates through inventory API
      const response = await axios.post('/api/v1/inventory/bulk', {
        propertyId: property?._id || propertyId,
        updates: allUpdates,
        timezone: 'Asia/Riyadh',
        modifiedBy: 'manual'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        alert('✅ تم تحديث التوفر بنجاح!');
        setAvailabilityList([]);
        setShowBulkRateModal(false);

        // Refresh calendar
        await fetchCalendarData();
      } else {
        throw new Error(response.data.message || 'فشل تحديث التوفر');
      }
    } catch (error) {
      console.error('❌ Error updating availability:', error);
      alert('خطأ: ' + (error.response?.data?.message || error.message));
    } finally {
      setAvailabilityUpdateStatus('idle');
    }
  };

  // Room name editing functions
  const startRoomNameEdit = (roomType) => {
    setEditingRoomId(roomType.id);
    setEditingRoomName(roomType.name);
    setRoomUpdateStatus('idle');
  };
  
  const cancelRoomNameEdit = () => {
    setEditingRoomId(null);
    setEditingRoomName('');
    setRoomUpdateStatus('idle');
  };
  
  const saveRoomName = async (roomTypeId) => {
    if (!editingRoomName.trim()) {
      alert('اسم الغرفة مطلوب');
      return;
    }
    
    setRoomUpdateStatus('syncing');
    
    try {
      await axios.put(
        `/api/v1/channex/room-type/${propertyId}/${roomTypeId}`,
        { title: editingRoomName.trim() },
        {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        }
      );

      // Update local state
      setChannexMapping(prev => ({
        ...prev,
        roomTypes: prev?.roomTypes?.map(rt => 
          rt.id === roomTypeId 
            ? { ...rt, name: editingRoomName.trim() }
            : rt
        ) || []
      }));
      
      setEditingRoomId(null);
      setEditingRoomName('');
      setRoomUpdateStatus('success');
      
      // Show success message briefly
      setTimeout(() => setRoomUpdateStatus('idle'), 2000);
    } catch (error) {
      console.error('Room name update error:', error);
      alert(`خطأ: ${error.message}`);
      setRoomUpdateStatus('error');
    }
  };

  // Sync room types and rate plans from Channex
  const syncRoomTypesFromChannex = async () => {
    if (!channexMapping?.propertyId) {
      setSyncRoomTypesMessage('يجب ربط العقار بـ Channex أولاً');
      return;
    }
    
    setSyncRoomTypesStatus('syncing');
    setSyncRoomTypesMessage('');
    
    try {
      const response = await axios.post(
        `/api/v1/channex/properties/${propertyId}/sync-room-types`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success) {
        // Update local state with synced data
        setChannexMapping(prev => ({
          ...prev,
          roomTypes: response.data.roomTypes || []
        }));
        
        setSyncRoomTypesStatus('success');
        setSyncRoomTypesMessage(`تم مزامنة ${response.data.roomTypes?.length || 0} أنواع غرف بنجاح`);
        
        // Reload the page to refresh property data
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        throw new Error(response.data.message || 'فشلت المزامنة');
      }
    } catch (error) {
      console.error('Failed to sync room types:', error);
      setSyncRoomTypesStatus('error');
      setSyncRoomTypesMessage('فشلت مزامنة أنواع الغرف: ' + (error.response?.data?.message || error.message));
    }
    
    // Clear status after 5 seconds
    setTimeout(() => {
      setSyncRoomTypesStatus('idle');
      setSyncRoomTypesMessage('');
    }, 5000);
  };
  
  // Rate plan name editing functions
  const startRatePlanNameEdit = (ratePlan) => {
    setEditingRatePlanId(ratePlan.id);
    setEditingRatePlanName(ratePlan.name);
    setRatePlanUpdateStatus('idle');
  };
  
  const cancelRatePlanNameEdit = () => {
    setEditingRatePlanId(null);
    setEditingRatePlanName('');
    setRatePlanUpdateStatus('idle');
  };

  const deleteRatePlan = async (roomTypeId, ratePlanId) => {
    const confirmDelete = window.confirm('هل أنت متأكد من حذف خطة السعر هذه؟ سيتم حذفها من جميع السجلات.');
    if (!confirmDelete) return;

    try {
      const response = await axios.delete(`/api/v1/properties/${property?._id || propertyId}/rate-plan/${ratePlanId}`, {
        params: {
          roomTypeId: roomTypeId
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        alert('تم حذف خطة السعر بنجاح');
        // Update the local state to reflect the deletion
        setChannexMapping(prev => ({
          ...prev,
          roomTypes: prev?.roomTypes?.map(rt => 
            rt.id === roomTypeId 
              ? {
                  ...rt,
                  ratePlans: rt.ratePlans?.filter(rp => rp.id !== ratePlanId) || []
                }
              : rt
          ) || []
        }));
      } else {
        alert('فشل حذف خطة السعر: ' + (response.data.message || 'خطأ غير معروف'));
      }
    } catch (error) {
      console.error('Error deleting rate plan:', error);
      alert('حدث خطأ أثناء حذف خطة السعر: ' + (error.response?.data?.message || error.message));
    }
  };
  
  const saveRatePlanName = async (ratePlanId) => {
    if (!editingRatePlanName.trim()) {
      alert('اسم خطة السعر مطلوب');
      return;
    }
    
    setRatePlanUpdateStatus('syncing');
    
    try {
      await secureApiService.updateRatePlan(
        propertyId,
        ratePlanId,
        { title: editingRatePlanName.trim() }
      );

      // Update local state - find and update the rate plan in the room types
      setChannexMapping(prev => ({
        ...prev,
        roomTypes: prev?.roomTypes?.map(roomType => ({
          ...roomType,
          ratePlans: roomType.ratePlans?.map(rp => 
            rp.id === ratePlanId 
              ? { ...rp, name: editingRatePlanName.trim() }
              : rp
          ) || []
        })) || []
      }));
      
      setEditingRatePlanId(null);
      setEditingRatePlanName('');
      setRatePlanUpdateStatus('success');
      
      // Show success message briefly
      setTimeout(() => setRatePlanUpdateStatus('idle'), 2000);
    } catch (error) {
      console.error('Rate plan name update error:', error);
      alert(`خطأ: ${error.message}`);
      setRatePlanUpdateStatus('error');
    }
  };

  // Calendar cell editing functions (kept for future use with old calendar view)
  // eslint-disable-next-line no-unused-vars
  const openEditModal = (type, roomType, dayIndex, currentValue, ratePlanId = '') => {
    // Use calendarWeekStart as base, not Date.now(), to match displayed dates
    const date = new Date(calendarWeekStart);
    date.setDate(date.getDate() + dayIndex);
    setEditingCell({
      type,
      roomTypeId: roomType?.id || null,
      roomTypeName: roomType?.name || 'شقة',
      dayIndex,
      date: date.toISOString().split('T')[0],
      currentValue,
      ratePlanId
    });
    setEditValue(currentValue.toString());
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      
      // Prepare the update data based on edit type
      const updateData = {
        propertyId: property._id,
        date: editingCell.date,
        roomTypeId: editingCell.roomTypeId,
        modifiedBy: 'manual'
      };
      
      // Prepare updates based on type
      let updates = [];
      
      switch(editingCell.type) {
        case 'availability':
          // Availability applies to room type, not rate plans
          updateData.inventory = { available: parseInt(editValue) || 0 };
          updates = [updateData];
          break;
          
        case 'minStay':
          // Restrictions apply to rate plans, similar to rates
          const roomMappingForMinStay = getChannexRoomMapping({ ...property, channexMapping }, editingCell.roomTypeId);
          if (roomMappingForMinStay?.ratePlans && roomMappingForMinStay.ratePlans.length > 0) {
            // Create an update for each rate plan
            updates = roomMappingForMinStay.ratePlans.map(ratePlan => ({
              date: editingCell.date,
              roomTypeId: editingCell.roomTypeId,
              ratePlanId: ratePlan.id,
              restrictions: { minStayArrival: parseInt(editValue) || 1 }
            }));
          } else {
            // Fallback to single update if no rate plans found
            updateData.restrictions = { minStayArrival: parseInt(editValue) || 1 };
            if (editingCell.ratePlanId) {
              updateData.ratePlanId = editingCell.ratePlanId;
            }
            updates = [updateData];
          }
          break;
          
        case 'rate':
          // For rates, update ALL rate plans for this room type
          const roomMapping = getChannexRoomMapping({ ...property, channexMapping }, editingCell.roomTypeId);
          if (roomMapping?.ratePlans && roomMapping.ratePlans.length > 0) {
            // Create an update for each rate plan
            updates = roomMapping.ratePlans.map(ratePlan => ({
              date: editingCell.date,
              roomTypeId: editingCell.roomTypeId,
              ratePlanId: ratePlan.id,
              rates: { standard: parseFloat(editValue) || 0 }
            }));
          } else {
            // Fallback to single update if no rate plans found
            updateData.rates = { standard: parseFloat(editValue) || 0 };
            if (editingCell.ratePlanId) {
              updateData.ratePlanId = editingCell.ratePlanId;
            }
            updates = [updateData];
          }
          break;

        default:
          console.error('Unknown edit type');
          break;
      }

      // Call the unified inventory API (Local-First)
      // Using unified endpoint for availability, rates, and restrictions
      const availabilityResponse = await axios.post('/api/v1/inventory/bulk', {
        propertyId: property?._id || propertyId,
        updates: updates, // Now can be multiple updates
        timezone: 'Asia/Riyadh',
        modifiedBy: 'manual'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!availabilityResponse.data.success) {
        throw new Error(availabilityResponse.data.message || 'Failed to update internal availability');
      }
      
      // Response data available via availabilityResponse.data if needed
      
      // NOTE: Channex sync now handled by backend after internal DB update
      // The backend will automatically sync to Channex if property is mapped
      // This follows the Local-First architecture pattern
      
      // Show success immediately (optimistic UI)
      setSyncMessage(`✅ تم تحديث ${editingCell.type === 'availability' ? 'التوفر' : editingCell.type === 'minStay' ? 'الحد الأدنى للإقامة' : 'السعر'} بنجاح`);
      
      // Clear message after 5 seconds
      setTimeout(() => setSyncMessage(''), 5000);
      
      // Refresh calendar data from internal DB
      await fetchCalendarData(calendarWeekStart);
      
      // Close modal and reset state
      setShowEditModal(false);
      setEditingCell({
        type: '',
        roomTypeId: '',
        roomTypeName: '',
        dayIndex: 0,
        date: '',
        currentValue: '',
        ratePlanId: ''
      });
      setEditValue('');
      
    } catch (error) {
      console.error('Failed to save edit:', error);
      setSyncMessage('❌ حدث خطأ أثناء الحفظ. الرجاء المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingCell({
      type: '',
      roomTypeId: '',
      roomTypeName: '',
      dayIndex: 0,
      date: '',
      currentValue: '',
      ratePlanId: ''
    });
    setEditValue('');
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="property-details-loading" dir="rtl">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
        <p>جاري تحميل تفاصيل العقار...</p>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="property-details-error" dir="rtl">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <Link to="/properties" className="btn btn-secondary">
          العودة إلى قائمة العقارات
        </Link>
      </div>
    );
  }
  
  // Property not found
  if (!property) {
    return (
      <div className="property-details-error" dir="rtl">
        <div className="alert alert-warning" role="alert">
          العقار غير موجود
        </div>
        <Link to="/properties" className="btn btn-secondary">
          العودة إلى قائمة العقارات
        </Link>
      </div>
    );
  }

  // Every link a guest could book this unit through: the OTA listing URLs that have been entered,
  // plus the direct-booking page when it is switched on for this unit.
  const bookingLinks = collectBookingLinks(property);
  const canEditProperty = user?.role === 'admin' || property.owner === user?.userId;

  const copyBookingLink = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLinkKey(key);
      setTimeout(() => setCopiedLinkKey(null), 2000);
    } catch {
      // Clipboard blocked (insecure origin or denied permission) — the URL is on screen and
      // selectable, so this is not an error state.
      setCopiedLinkKey(null);
    }
  };

  return (
    <div className="property-details" dir="rtl">
      {/* Header Section */}
      <div className="property-details-header">
        <div className="header-content">
          <h1>{property.name}</h1>
          <div className="header-actions">
            <Link to="/properties" className="btn btn-secondary">
              <i className="bi bi-arrow-left"></i> العودة إلى قائمة العقارات
            </Link>
            
            {/* Edit button - only for admins and property owners */}
            {(user?.role === 'admin' || property.owner === user?.userId) && (
              <Link to={`/properties/${property?._id || propertyId}/edit`} className="btn btn-primary">
                <i className="bi bi-pencil"></i> تعديل العقار
              </Link>
            )}
            
            <button onClick={handleRefreshProperty} className="btn btn-info">
              <i className="bi bi-arrow-clockwise"></i> تحديث البيانات
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Action Buttons - Only visible on mobile */}
      <div className="mobile-actions">
        <Link to="/properties" className="btn btn-secondary">
          <i className="bi bi-arrow-left"></i> العودة إلى قائمة العقارات
        </Link>
        
        {/* Edit button - only for admins and property owners */}
        {(user?.role === 'admin' || property.owner === user?.userId) && (
          <Link to={`/properties/${property._id}/edit`} className="btn btn-primary">
            <i className="bi bi-pencil"></i> تعديل العقار
          </Link>
        )}
        
        <button onClick={handleRefreshProperty} className="btn btn-info">
          <i className="bi bi-arrow-clockwise"></i> تحديث البيانات
        </button>
      </div>

      {/* Property Info Summary */}
      <div className="property-info-summary">
        {/* Property Name Header */}
        <div className="property-name-header">
          <h2>{property.name}</h2>
          {property.propertyCode && (
            <span className="badge bg-light text-dark ms-2" dir="ltr">{property.propertyCode}</span>
          )}
        </div>

        {/* Property Details Grid */}
        <div className="property-details-grid">
          <div className="info-item">
            <i className="bi bi-geo-alt"></i>
            <span className="info-value">
              {property.city && `${property.city} - `}{property.address || 'غير محدد'}
            </span>
          </div>
          <div className="info-item">
            <i className="bi bi-house"></i>
            <span className="info-value">{property.type || 'غير محدد'}</span>
          </div>

          {/* ─── The two dates owners keep asking us for ──────────────────────────────────────
              tasks/property-contract-onboarded-dates.md. Placed with the location and type — the
              things about this unit that simply ARE — rather than beside the platform and status
              chips below, which report what is happening to it right now.

              Record data. Nothing reads either date: not billing, not the owner report, not the
              commission worksheet. They exist so an owner asking "when did we start?" gets an
              answer without ops opening a spreadsheet.

              ⚠️ ONE quiet line, deliberately a size and a shade below its neighbours — first cut
              gave them two full-weight chips and they read as loud as «حالة التشغيل», which ops
              changes daily. Not collapsed behind a toggle either: an owner who has to find a
              control to answer "when did we start" is back to phoning ops, which is the problem
              this replaces. Small and always visible beats hidden and correct. */}
          <div className="info-item record-dates-item">
            <i className="bi bi-file-earmark-text"></i>

            {/* «بداية العقد» renders only when the backend put it in the response (D4). The field
                is `select: false` and the property read attaches it for core staff and this unit's
                own owner only, so the ABSENCE of the key IS the permission decision — there is no
                role list duplicated here to drift out of step with the server. */}
            {'contractStartDate' in property && (
              <span className="record-date">
                <span className="info-label">بداية العقد:</span>

                {editingContractStart ? (
                  <>
                    <input
                      type="date"
                      className="form-control form-control-sm d-inline-block w-auto ms-2"
                      value={contractStartDraft}
                      onChange={(e) => setContractStartDraft(e.target.value)}
                      disabled={savingContractStart}
                      style={{ maxWidth: '160px' }}
                      autoFocus
                    />
                    <button
                      className="btn btn-success btn-sm ms-2"
                      onClick={saveContractStart}
                      disabled={savingContractStart}
                    >
                      {savingContractStart ? 'جاري الحفظ...' : 'حفظ'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm ms-1"
                      onClick={cancelContractStartEdit}
                      disabled={savingContractStart}
                    >
                      إلغاء
                    </button>
                    {contractStartError && (
                      <small className="text-danger d-block mt-1">{contractStartError}</small>
                    )}
                  </>
                ) : (
                  <>
                    {/* «غير مسجل» — not recorded — rather than «غير محدد», and never the onboarded
                        date as a stand-in. Two live units genuinely have no contract date, and a
                        borrowed one would read as fact. */}
                    <span className="info-value">
                      {property.contractStartDate ? formatLongDate(property.contractStartDate) : 'غير مسجل'}
                    </span>
                    {canEditContractStart && (
                      <button
                        type="button"
                        className="record-date-edit"
                        onClick={startContractStartEdit}
                        title={property.contractStartDate ? 'تعديل بداية العقد' : 'تسجيل بداية العقد'}
                      >
                        ✏️
                      </button>
                    )}
                  </>
                )}
              </span>
            )}

            {/* «انضم للمنصة» is `createdAt` (D1). There is deliberately no `onboardedAt` field: on
                2026-09-08 all 12 prod units' `createdAt` matched the ops record of the onboarded
                date to the day, and a second copy of a fact only exists to disagree with the first.
                ⚠️ Never relabel this «تاريخ الإنشاء» — the label carries the business meaning. */}
            <span className="record-date">
              <span className="info-label">انضم للمنصة:</span>
              <span className="info-value">{formatLongDate(property.createdAt)}</span>
            </span>
          </div>

          {/* Connected Platforms */}
          {((channelInfo?.channels && channelInfo.channels.length > 0) ||
            (property.platformAccounts && Object.keys(property.platformAccounts).length > 0) ||
            property.gathernMapping?.status === 'active') && (
            <div className="info-item platforms-item">
              <i className="bi bi-link-45deg"></i>
              <span className="info-label">المنصات المتصلة:</span>
              <div className="platforms-badges">
                {/* Display Channex channels if available */}
                {channelInfo?.channels && channelInfo.channels.map((channel) => (
                  <span
                    key={channel.channelId}
                    className="platform-badge"
                    style={{ backgroundColor: getPlatformColor(channel.platform) }}
                    title={channel.status === 'active' ? 'متزامن عبر Channex' : 'غير نشط'}
                  >
                    {channel.platform === 'airbnb' ? 'Airbnb' :
                     channel.platform === 'booking' ? 'Booking.com' :
                     channel.platform === 'expedia' ? 'Expedia' :
                     channel.platform.charAt(0).toUpperCase() + channel.platform.slice(1)}
                  </span>
                ))}

                {/* Display local platforms from platformAccounts (exclude global OTAs managed by Channex and Gathern, which has its own mapping) */}
                {property.platformAccounts &&
                  Object.entries(property.platformAccounts)
                    .filter(([platformKey]) => !['booking', 'airbnb', 'expedia', 'gathern'].includes(platformKey.toLowerCase()))
                    .map(([platformKey, platformData]) => (
                  <span
                    key={platformKey}
                    className="platform-badge"
                    style={{ backgroundColor: getPlatformColor(platformKey) }}
                    title={platformData.status === 'active' ? 'متزامن' : 'غير نشط'}
                  >
                    {platformKey.charAt(0).toUpperCase() + platformKey.slice(1)}
                  </span>
                ))}

                {/* Display Gathern from gathernMapping (single source of truth) */}
                {property.gathernMapping?.status === 'active' && (
                  <span
                    className="platform-badge"
                    style={{ backgroundColor: getPlatformColor('gathern') }}
                    title="متزامن عبر iCal"
                  >
                    Gathern
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ─── روابط الحجز — where a guest can book this unit ─────────────────────────────────
              tasks/property-public-booking-links.md.

              Rendered UNCONDITIONALLY, unlike the connected-platforms row above it: a unit can be
              listed on an OTA while its channel is disconnected, and ops still needs the link.
              A single collapsed control, because the info card is read at a glance every day and
              four URLs expanded into it would bury the operational status beside it. */}
          <div className="info-item booking-links-item" ref={bookingLinksRef}>
            <button
              type="button"
              className="booking-links-trigger"
              onClick={() => setBookingLinksOpen((open) => !open)}
              aria-expanded={bookingLinksOpen}
            >
              <i className="bi bi-link-45deg"></i>
              <span>روابط الحجز</span>
              {bookingLinks.length > 0 && (
                <span className="booking-links-count">{bookingLinks.length}</span>
              )}
              <i className={`bi ${bookingLinksOpen ? 'bi-chevron-up' : 'bi-chevron-down'} booking-links-chevron`}></i>
            </button>

            {bookingLinksOpen && (
              <div className="booking-links-panel" dir="rtl">
                {bookingLinks.length === 0 && !property.features?.directBookingEnabled ? (
                  <p className="booking-links-empty">
                    لم تُضف روابط الحجز لهذه الوحدة بعد.
                    {canEditProperty && ' أضِفها من صفحة «تعديل العقار».'}
                  </p>
                ) : (
                  <>
                    <ul className="booking-links-list">
                      {bookingLinks.map(({ key, label, url }) => (
                        <li className="booking-link-row" key={key}>
                          <span
                            className="booking-link-dot"
                            style={{ backgroundColor: getPlatformColor(key) }}
                            aria-hidden="true"
                          />
                          <div className="booking-link-text">
                            <span className="booking-link-label">{label}</span>
                            {/* Forced LTR: a listing URL read right-to-left is unreadable, and an
                                operator checking it against an extranet needs it character-exact. */}
                            <span className="booking-link-url" dir="ltr">{url}</span>
                          </div>
                          <div className="booking-link-actions">
                            <button
                              type="button"
                              className="booking-link-action"
                              onClick={() => copyBookingLink(key, url)}
                              title="نسخ الرابط"
                              aria-label={`نسخ رابط ${label}`}
                            >
                              <i className={`bi ${copiedLinkKey === key ? 'bi-check2' : 'bi-clipboard'}`}></i>
                            </button>
                            <a
                              className="booking-link-action"
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="فتح الرابط"
                              aria-label={`فتح رابط ${label}`}
                            >
                              <i className="bi bi-box-arrow-up-left"></i>
                            </a>
                          </div>
                        </li>
                      ))}
                    </ul>

                    {/* The direct-booking link is derived, not stored — so its OFF state is shown
                        rather than hidden, or ops would read the panel as "we have no direct link"
                        when it is one toggle away. */}
                    {!property.features?.directBookingEnabled && (
                      <p className="booking-links-direct-off">
                        الحجز المباشر غير مفعّل لهذه الوحدة.
                      </p>
                    )}

                    {bookingLinks.length > 0 && (
                      <div className="booking-links-footer">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => copyBookingLink('all', formatBookingLinksForSharing(bookingLinks))}
                        >
                          <i className="bi bi-clipboard-check"></i> نسخ الكل
                        </button>
                        {copiedLinkKey === 'all' && (
                          <span className="booking-links-copied">تم نسخ جميع الروابط</span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* «حالة التشغيل» — derived on every read, not a stored field (tasks/unit-operational-status.md).
            Full width below the facts grid: it is the one line in this header that changes during the
            day. A stop or resume changes stop sale, so the calendar tab is refreshed with it. */}
        <UnitStatusPanel
          propertyId={property._id}
          canManage={canUpdateOperationalStatus}
          onChanged={() => fetchCalendarData(calendarCurrentDate)}
        />
      </div>

      {/* Tabs Navigation */}
      <div className="property-tabs">
        <nav className="tabs-nav">
          {/* Calendar Tab - Visible to admin, staff, and turnKey owners */}
          {(user?.role === 'admin' || user?.role === 'ops' || user?.role === 'ops-manager' || user?.role === 'maintenance' || user?.role === 'maintenance-supervisor' || user?.role === 'turnKeyOwner') && property?._id && (
            <button
              className={`tab-button touch-tab ${activeTab === 'calendar' ? 'active' : ''}`}
              onClick={() => setActiveTab('calendar')}
            >
              التقويم
            </button>
          )}
          <button
            className={`tab-button touch-tab ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
          >
            الحجوزات
          </button>
          {/* Maintenance Tab - Hidden from turnKey owners (operations manages for them) */}
          {(user?.role === 'admin' || user?.role === 'ops-manager' || user?.role === 'maintenance' || user?.role === 'maintenance-supervisor' || user?.role === 'ops') && (
            <button
              className={`tab-button touch-tab ${activeTab === 'maintenance' ? 'active' : ''}`}
              onClick={() => setActiveTab('maintenance')}
            >
              الصيانة والنظافة
            </button>
          )}
          {/* Channex Integration Tab - Only visible to admin */}
          {/* Rooms & Rates Tab - Only for hotels, not apartments */}
          {user?.role === 'admin' && !isApartment && (
            <button
              className={`tab-button touch-tab ${activeTab === 'channex' ? 'active' : ''}`}
              onClick={() => setActiveTab('channex')}
            >
              الأسعار والتوفر
            </button>
          )}
          {/* Reviews Tab - Visible to core staff (admin, ops, ops-manager) and turnKey owners */}
          {(CORE_STAFF_ROLES.includes(user?.role) || user?.role === 'turnKeyOwner') && (
            <button
              className={`tab-button touch-tab ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              التقييمات
            </button>
          )}
          {/* Locks Tab - Admin only (Phase 1: read-only TTLock observability) */}
          {user?.role === 'admin' && (
            <button
              className={`tab-button touch-tab ${activeTab === 'locks' ? 'active' : ''}`}
              onClick={() => setActiveTab('locks')}
            >
              {lockT('locks.tabLabel')}
            </button>
          )}
        </nav>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <div className="bookings-tab">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>سجل الحجوزات</h3>
                {/* Action buttons - Hidden from turnKey owners */}
                {(user?.role === 'admin' || user?.role === 'ops-manager') && (
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-primary touch-btn"
                      onClick={handleSyncBookings}
                      disabled={bookingSyncStatus === 'syncing'}
                    >
                      {bookingSyncStatus === 'syncing' ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          مزامنة...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-repeat me-2"></i>
                          مزامنة الحجوزات
                        </>
                      )}
                    </button>
                    <button
                      className="btn btn-primary touch-btn"
                      onClick={() => setShowBookingModal(true)}
                    >
                      <i className="bi bi-plus-circle"></i> حجز يدوي
                    </button>
                  </div>
                )}
              </div>
              {bookingSyncMessage && (
                <div className={`alert ${bookingSyncStatus === 'success' ? 'alert-success' : 'alert-danger'} mb-2`}>
                  {bookingSyncMessage}
                </div>
              )}

              {/* Bookings Filters */}
              <div className="bookings-filters mb-4" style={{
                backgroundColor: '#f8f9fa',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <h5 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 'bold' }}>تصفية الحجوزات</h5>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>البحث</label>
                    <input
                      type="text"
                      placeholder="بحث بالاسم أو رقم التأكيد"
                      value={bookingsFilters.search}
                      onChange={(e) => setBookingsFilters({...bookingsFilters, search: e.target.value})}
                      className="form-control form-control-sm touch-input"
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>الحالة</label>
                    <select
                      value={bookingsFilters.status}
                      onChange={(e) => setBookingsFilters({...bookingsFilters, status: e.target.value})}
                      className="form-select form-select-sm touch-select"
                    >
                      <option value="">جميع الحالات</option>
                      <option value="upcoming">حجز مؤكد</option>
                      <option value="active">نشط</option>
                      <option value="completed">مكتمل</option>
                      <option value="cancelled">ملغي</option>
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>المنصة</label>
                    <select
                      value={bookingsFilters.platform}
                      onChange={(e) => setBookingsFilters({...bookingsFilters, platform: e.target.value})}
                      className="form-select form-select-sm touch-select"
                    >
                      <option value="">جميع المنصات</option>
                      <option value="airbnb">Airbnb</option>
                      <option value="booking">Booking.com</option>
                      <option value="hostme">Host Me</option>
                      <option value="gathern">GatherN</option>
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>الفترة الزمنية</label>
                    <select
                      value={bookingsFilters.dateRange}
                      onChange={(e) => setBookingsFilters({...bookingsFilters, dateRange: e.target.value})}
                      className="form-select form-select-sm touch-select"
                    >
                      {DATE_RANGE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  {bookingsFilters.dateRange === DATE_RANGE_CUSTOM && (
                    <>
                      <div className="col-md-3">
                        <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>من</label>
                        <input
                          type="date"
                          value={bookingsFilters.dateFrom}
                          max={bookingsFilters.dateTo || undefined}
                          onChange={(e) => setBookingsFilters({...bookingsFilters, dateFrom: e.target.value})}
                          className="form-control form-control-sm touch-input"
                        />
                      </div>

                      <div className="col-md-3">
                        <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>إلى</label>
                        <input
                          type="date"
                          value={bookingsFilters.dateTo}
                          min={bookingsFilters.dateFrom || undefined}
                          onChange={(e) => setBookingsFilters({...bookingsFilters, dateTo: e.target.value})}
                          className="form-control form-control-sm touch-input"
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <button
                    onClick={resetBookingsFilters}
                    className="btn btn-sm btn-outline-secondary touch-btn"
                  >
                    <i className="bi bi-x-circle"></i> إعادة تعيين
                  </button>
                  <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                    عرض {filteredBookings.length} من {mvpBookings?.length || 0} حجز
                  </span>
                </div>
              </div>

              {bookingsLoading ? (
                <div className="text-center py-4" role="status">
                  <div className="spinner-border text-primary"></div>
                  <p className="mt-2 text-muted">{lockT('states.loading')}</p>
                </div>
              ) : bookingsError ? (
                // A failed read must not say «لا توجد حجوزات» (step 7)
                <div className="alert alert-danger" role="alert">
                  {lockT('unitDetails.bookingsLoadError')}
                  <button type="button" className="btn btn-outline-danger btn-sm touch-btn"
                    style={{ marginInlineStart: '12px' }} onClick={() => refetchBookings()}>
                    {lockT('states.retry')}
                  </button>
                </div>
              ) : (() => {
                // Use filtered bookings instead of all bookings
                const allBookings = filteredBookings;
                
                return allBookings.length > 0 ? (
                  <div className="bookings-list">
                    <table className="bookings-table">
                      <thead>
                        <tr>
                          <th>رقم الحجز</th>
                          <th>تاريخ الوصول</th>
                          <th>تاريخ المغادرة</th>
                          <th className="hide-mobile-sm">عدد الليالي</th>
                          <th>المنصة</th>
                          <th>اسم الضيف</th>
                          <th>الحالة</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allBookings.map((booking, index) => (
                        <tr
                          key={index}
                          onClick={() => navigate(`/bookings/${booking._id}`)}
                          style={{ cursor: 'pointer' }}
                          className="booking-row"
                        >
                          <td>
                            {booking.confirmationCode ||
                             booking.enrichedData?.confirmationCode ||
                             (booking.sourcePlatform === 'direct' ? `HM-${booking._id.slice(-8).toUpperCase()}` : 'غير متوفر')}
                          </td>
                          <td>{formatDate(booking.checkIn || booking.startDate)}</td>
                          <td>{formatDate(booking.checkOut || booking.endDate)}</td>
                          <td className="hide-mobile-sm">{calculateNights(booking.checkIn || booking.startDate, booking.checkOut || booking.endDate)}</td>
                          <td>
                            <div className="platform-cell">
                              {getPlatformLogo(booking.sourcePlatform || booking.platform) ? (
                                <img
                                  src={getPlatformLogo(booking.sourcePlatform || booking.platform)}
                                  alt={booking.sourcePlatform || booking.platform}
                                  className="platform-logo"
                                  title={getPlatformDisplayName(booking.sourcePlatform || booking.platform)}
                                />
                              ) : (
                                <span className="platform-badge-text">
                                  <i className={`bi ${getPlatformIcon(booking.sourcePlatform || booking.platform)}`}></i>
                                  {getPlatformDisplayName(booking.sourcePlatform || booking.platform)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{booking.guestName || booking.summary || 'غير محدد'}</td>
                          <td>
                            <span className={`booking-status ${booking.status || 'upcoming'}`}>
                              {booking.status === 'upcoming' ? 'حجز مؤكد' :
                               booking.status === 'active' ? 'حجز نشط' :
                               booking.status === 'completed' ? 'مكتمل' :
                               booking.status === 'cancelled' ? 'ملغي' :
                               'حجز مؤكد'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-bookings">
                  <p>لا توجد حجوزات مسجلة حالياً</p>
                </div>
              );
            })()}
            </div>
          )}

          {/* Maintenance & Cleaning Tab */}
          {activeTab === 'maintenance' && (
            <div className="maintenance-tab">
              <h3 className="mb-4">سجل الصيانة والنظافة</h3>
              {/* Simple status dashboard for everyone - owners and staff */}
              {/* Full task management is available in dedicated /tasks page */}
              <OwnerTaskDashboard propertyId={property._id} />
            </div>
          )}

          {/* Reviews Tab - Core staff (admin/ops/ops-manager) + turnKey owners */}
          {activeTab === 'reviews' && (CORE_STAFF_ROLES.includes(user?.role) || user?.role === 'turnKeyOwner') && (
            <div className="reviews-tab">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>إدارة التقييمات</h3>
                {reviewSubTab === 'direct' && reviewStats && (
                  <div className="d-flex align-items-center gap-2" style={{fontSize: '1.1rem'}}>
                    <i className="bi bi-star-fill" style={{color: '#FFD700'}}></i>
                    <span style={{fontWeight: '600'}}>{reviewStats.averageRating.toFixed(1)}</span>
                    <span style={{color: '#666'}}>({reviewStats.totalReviews} تقييمات)</span>
                  </div>
                )}
              </div>

              {/* Sub-tab pills: direct-booking vs external-platform (OTA) reviews */}
              <div className="mb-4" style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                <button
                  type="button"
                  className={`btn btn-sm ${reviewSubTab === 'direct' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setReviewSubTab('direct')}
                >
                  <i className="bi bi-house-door me-1"></i>
                  الحجوزات المباشرة
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${reviewSubTab === 'ota' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setReviewSubTab('ota')}
                >
                  <i className="bi bi-globe2 me-1"></i>
                  تقييمات المنصات الخارجية
                </button>
              </div>

              {reviewSubTab === 'direct' && (
              <>
              {/* Scope Clarification Banner */}
              <div className="alert alert-info" role="alert" style={{marginBottom: '20px'}}>
                <div className="d-flex align-items-start gap-2">
                  <i className="bi bi-info-circle-fill" style={{fontSize: '1.2rem', marginTop: '2px'}}></i>
                  <div>
                    <strong>ملاحظة:</strong> هذا القسم خاص بتقييمات الحجوزات المباشرة (من صفحة الحجز الخاصة بك).
                    <br />
                    <small className="text-muted">لعرض تقييمات Airbnb و Booking.com، افتح تبويب «تقييمات المنصات الخارجية» بالأعلى.</small>
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="mb-4" style={{display: 'flex', gap: '12px', flexWrap: 'wrap'}}>
                <select
                  className="form-select"
                  style={{width: 'auto'}}
                  value={reviewFilters.status}
                  onChange={(e) => setReviewFilters({...reviewFilters, status: e.target.value})}
                >
                  <option value="all">جميع التقييمات</option>
                  <option value="pending">قيد الانتظار</option>
                  <option value="approved">معتمدة</option>
                  <option value="rejected">مرفوضة</option>
                </select>

                <select
                  className="form-select"
                  style={{width: 'auto'}}
                  value={reviewFilters.rating}
                  onChange={(e) => setReviewFilters({...reviewFilters, rating: e.target.value})}
                >
                  <option value="">جميع التقييمات</option>
                  <option value="5">5 نجوم</option>
                  <option value="4">4 نجوم</option>
                  <option value="3">3 نجوم</option>
                  <option value="2">2 نجوم</option>
                  <option value="1">1 نجمة</option>
                </select>
              </div>

              {/* Reviews List */}
              {reviewsLoading ? (
                <div style={{textAlign: 'center', padding: '40px'}}>
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">جاري التحميل...</span>
                  </div>
                </div>
              ) : reviewsError ? (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {reviewsError}
                  <button
                    className="btn btn-sm btn-outline-danger ms-3"
                    onClick={fetchReviews}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    إعادة المحاولة
                  </button>
                </div>
              ) : reviews.length > 0 ? (
                <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                  {reviews.map((review) => (
                    <div
                      key={review._id}
                      className="card"
                      style={{
                        borderRight: review.status === 'pending' ? '4px solid #FFC107' :
                                    review.status === 'approved' ? '4px solid #4CAF50' :
                                    '4px solid #F44336'
                      }}
                    >
                      <div className="card-body">
                        {/* Review Header */}
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h5 className="mb-1">{review.guestName}</h5>
                            <small className="text-muted">
                              {formatLongDate(review.createdAt)}
                            </small>
                            <div className="mt-1">
                              {[...Array(5)].map((_, i) => (
                                <i
                                  key={i}
                                  className={i < review.rating ? 'bi bi-star-fill' : 'bi bi-star'}
                                  style={{color: i < review.rating ? '#FFD700' : '#ccc'}}
                                ></i>
                              ))}
                            </div>
                          </div>
                          <div>
                            <span className={`badge ${
                              review.status === 'pending' ? 'bg-warning' :
                              review.status === 'approved' ? 'bg-success' :
                              'bg-danger'
                            }`}>
                              {review.status === 'pending' ? 'قيد الانتظار' :
                               review.status === 'approved' ? 'معتمد' :
                               'مرفوض'}
                            </span>
                          </div>
                        </div>

                        {/* Review Comment */}
                        <p className="mb-3" style={{lineHeight: '1.6'}}>
                          {review.comment}
                        </p>

                        {/* Owner Response */}
                        {review.response?.text && (
                          <div style={{
                            padding: '12px',
                            backgroundColor: '#f8f9fa',
                            borderRight: '3px solid #007bff',
                            borderRadius: '4px',
                            marginBottom: '12px'
                          }}>
                            <div style={{fontWeight: '600', fontSize: '0.9rem', marginBottom: '6px'}}>
                              رد المالك:
                            </div>
                            <p style={{margin: 0, fontSize: '0.9rem'}}>
                              {review.response.text}
                            </p>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="d-flex gap-2">
                          {/* Approve/reject are admin-only server-side (reviewRoutes.js) —
                              gate the buttons to match, or ops gets an unexplained failure. */}
                          {review.status === 'pending' && user?.role === 'admin' && (
                            <>
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => approveReview(review._id)}
                                disabled={reviewActionLoading}
                              >
                                <i className="bi bi-check-circle me-1"></i>
                                موافقة
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => rejectReview(review._id)}
                                disabled={reviewActionLoading}
                              >
                                <i className="bi bi-x-circle me-1"></i>
                                رفض
                              </button>
                            </>
                          )}
                          {review.status === 'approved' && !review.response?.text && (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setSelectedReview(review);
                                setShowReviewResponseModal(true);
                              }}
                              disabled={reviewActionLoading}
                            >
                              <i className="bi bi-reply me-1"></i>
                              إضافة رد
                            </button>
                          )}
                          {user?.role === 'admin' && (
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => deleteReview(review._id)}
                              disabled={reviewActionLoading}
                            >
                              <i className="bi bi-trash me-1"></i>
                              حذف
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  backgroundColor: '#fafafa',
                  borderRadius: '8px'
                }}>
                  <i className="bi bi-star" style={{fontSize: '3rem', color: '#ddd', marginBottom: '16px'}}></i>
                  <p style={{color: '#666'}}>لا توجد تقييمات</p>
                </div>
              )}
              </>
              )}

              {/* External-platform (OTA) reviews — read-only */}
              {reviewSubTab === 'ota' && (
                <>
                  <div className="alert alert-info" role="alert" style={{marginBottom: '20px'}}>
                    <div className="d-flex align-items-start gap-2">
                      <i className="bi bi-info-circle-fill" style={{fontSize: '1.2rem', marginTop: '2px'}}></i>
                      <div>
                        تقييمات الضيوف من <strong>Airbnb</strong> و <strong>Booking.com</strong>.
                        <br />
                        {/* Owners never reply. Ops replies to a switched-on unit's reviews from «التقييمات»
                            (post-checkout review Phase 3); every other unit is still answered on the platform. */}
                        <small className="text-muted">
                          {!CORE_STAFF_ROLES.includes(user?.role)
                            ? 'للاطلاع فقط.'
                            : property?.guestReviewAutomationEnabled
                              ? <>يتم الرد على هذه التقييمات من صفحة <Link to="/ops/reviews?list=replies">«التقييمات والشكاوى»</Link>.</>
                              : 'هذه التقييمات للعرض فقط — يتم الرد عليها مباشرةً على المنصة.'}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Per-OTA filter — only when the unit has more than one platform */}
                  {otaAvailableSources.length > 1 && (
                    <div className="mb-4" style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                      <button
                        type="button"
                        className={`btn btn-sm ${otaEffectiveFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setOtaSourceFilter('all')}
                      >
                        الكل
                      </button>
                      {otaAvailableSources.map((src) => (
                        <button
                          key={src}
                          type="button"
                          className={`btn btn-sm ${otaEffectiveFilter === src ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setOtaSourceFilter(src)}
                        >
                          {getOtaBrand(src).label}
                        </button>
                      ))}
                    </div>
                  )}

                  {otaReviewsLoading ? (
                    <div style={{textAlign: 'center', padding: '40px'}}>
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">جاري التحميل...</span>
                      </div>
                    </div>
                  ) : otaReviewsError ? (
                    <div className="alert alert-danger" role="alert">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {otaReviewsError}
                      <button
                        className="btn btn-sm btn-outline-danger ms-3"
                        onClick={fetchOtaReviews}
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        إعادة المحاولة
                      </button>
                    </div>
                  ) : otaReviews.length > 0 ? (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                      {otaVisibilityError && (
                        <div className="alert alert-warning mb-0" role="alert">{otaVisibilityError}</div>
                      )}
                      {filteredOtaReviews.map((review) => (
                        <OtaReviewCard
                          key={review._id}
                          review={review}
                          onToggleWebsite={CORE_STAFF_ROLES.includes(user?.role) ? toggleReviewOnWebsite : undefined}
                        />
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '60px 20px',
                      backgroundColor: '#fafafa',
                      borderRadius: '8px'
                    }}>
                      <i className="bi bi-chat-square-text" style={{fontSize: '3rem', color: '#ddd', marginBottom: '16px'}}></i>
                      <p style={{color: '#666'}}>لا توجد تقييمات من المنصات الخارجية بعد</p>
                    </div>
                  )}

                  {otaReviews.length > 0 && otaTotal > otaReviews.length && (
                    <p className="text-muted text-center mt-3" style={{fontSize: '0.85rem'}}>
                      يتم عرض أحدث {otaReviews.length} تقييم من إجمالي {otaTotal}.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Locks Tab — Phase 1: read-only TTLock observability (admin only) */}
          {activeTab === 'locks' && user?.role === 'admin' && property?._id && (
            <div className="locks-tab">
              {/* ─── رابط لوحة تسجيل الخروج — the plaque URL for this unit ──────────────────────
                  tasks/checkout-plaque-panel.md D65–D69.

                  🔴 A WALL-MOUNTED CREDENTIAL, shown to admin / ops-manager only (D66). Never
                  rendered for owners and never part of the property payload — it arrives from its
                  own endpoint, which enforces the same two roles regardless of what is hidden here.

                  Lives in this tab, and COLLAPSED, because it is needed exactly once per unit —
                  when a plaque is commissioned — and the property page is visited daily. It sits
                  beside the lock because it is the same object to whoever is standing at the door.
                  ⚠️ This tab is admin-only, so an ops-manager cannot currently reach it; widening
                  that gate means widening access to the lock controls too, which is a separate call.

                  Deliberately NOT a status indicator (D69): this shows the string, nothing more. A
                  green tick here would be read as "the plaque works", and whether it works is a
                  question about a certificate, a phone and a door — not about this screen. */}
              {(user?.role === 'admin' || user?.role === 'ops-manager') && (
                <div className="plaque-link-card" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                  <button
                    type="button"
                    className="plaque-link-toggle"
                    onClick={() => setPlaqueOpen((open) => !open)}
                    aria-expanded={plaqueOpen}
                  >
                    <i className="bi bi-tag"></i>
                    <span>{lockT('locks.plaque.heading')}</span>
                    <i className={`bi ${plaqueOpen ? 'bi-chevron-up' : 'bi-chevron-down'} plaque-link-chevron`}></i>
                  </button>

                  {plaqueOpen && (
                    <div className="plaque-link-body">
                      {plaqueLink && (
                        <>
                          <div className="plaque-link-value">{plaqueLink.url}</div>
                          <div className="plaque-link-actions">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(plaqueLink.url);
                                  setPlaqueCopied(true);
                                  setTimeout(() => setPlaqueCopied(false), 2000);
                                } catch {
                                  // Clipboard blocked (insecure origin, or permission denied) — the
                                  // URL is on screen and selectable, so this is not an error state.
                                  setPlaqueCopied(false);
                                }
                              }}
                            >
                              <i className="bi bi-clipboard"></i> {lockT('locks.plaque.copy')}
                            </button>

                            {/* ─── تنزيل رمز QR — tasks/checkout-plaque-artwork-generator.md D73–D82.

                                We produce the CODE; the designer produces the plaque. This hands over
                                one vector file per apartment, named for the apartment, and that
                                filename is the whole pairing mechanism — there is no list for it to
                                fall out of step with.

                                🔴 Addressed by APARTMENT, never by URL (D76). There is no text input
                                anywhere in this feature: the URL comes from the endpoint that already
                                resolved this unit's token, so an operator cannot pair `P-0001` with
                                `P-0002`'s code by mistyping. That failure is undetectable once a
                                plaque is on a wall, and this is the control against it.

                                Verification is not a checksum — it is the plaque page itself, which
                                names the apartment on every state. Scan the printed code and it tells
                                you which unit it belongs to. */}
                            {plaqueLink.propertyCode ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  const contents = toPrintableSvg(plaqueQrRef.current?.querySelector('svg'));
                                  if (!contents) {
                                    setPlaqueQrError(true);
                                    return;
                                  }
                                  setPlaqueQrError(false);
                                  downloadSvg(`${plaqueLink.propertyCode}.svg`, contents);
                                }}
                              >
                                <i className="bi bi-download"></i> {lockT('locks.plaque.download')}
                              </button>
                            ) : null}

                            {plaqueCopied && (
                              <span className="plaque-link-copied">{lockT('locks.plaque.copied')}</span>
                            )}
                          </div>

                          {/* Rendered off-screen, never shown. The operator needs the FILE, not a
                              picture of the code — and a code on screen invites someone to photograph
                              it and skip the file, which is how the printed symbol stops matching the
                              one we generated. Error correction and quiet zone are set explicitly:
                              the library defaults to L and to no quiet zone, and both defaults would
                              produce a code that scans on a desk and fails in a corridor. */}
                          <div ref={plaqueQrRef} style={{ display: 'none' }} aria-hidden="true">
                            <QRCodeSVG
                              value={plaqueLink.url}
                              level={QR_ERROR_CORRECTION}
                              marginSize={QR_QUIET_ZONE_MODULES}
                              size={512}
                            />
                          </div>

                          <p className="plaque-link-note">{lockT('locks.plaque.note')}</p>
                          <p className="plaque-link-note">
                            {plaqueLink.propertyCode
                              ? lockT('locks.plaque.downloadNote')
                              : lockT('locks.plaque.downloadNoCode')}
                          </p>
                          {plaqueQrError && (
                            <p className="plaque-link-note plaque-link-note-warn">
                              {lockT('locks.plaque.downloadFailed')}
                            </p>
                          )}
                        </>
                      )}

                      {plaqueError === 'no-token' && (
                        <p className="plaque-link-note plaque-link-note-warn">
                          {lockT('locks.plaque.noToken')}
                        </p>
                      )}

                      {plaqueError === 'failed' && (
                        <p className="plaque-link-note plaque-link-note-warn">
                          {lockT('locks.plaque.failed')}
                        </p>
                      )}

                      {!plaqueLink && !plaqueError && (
                        <p className="plaque-link-note">{lockT('locks.plaque.loading')}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <ErrorBoundary>
                <LockStatusPanel propertyId={property._id} />
              </ErrorBoundary>
            </div>
          )}

          {/* Channex Integration Tab - Only visible to admin */}
          {activeTab === 'channex' && user?.role === 'admin' && (
            <div className="channex-tab">
              <div className="channex-tab-header">
                <h3>إدارة الأسعار والتوفر</h3>
                {channexMapping?.propertyId && (
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={syncRoomTypesFromChannex}
                      disabled={syncRoomTypesStatus === 'syncing'}
                      title="مزامنة أنواع الغرف وخطط الأسعار"
                    >
                      {syncRoomTypesStatus === 'syncing' ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          جاري المزامنة...
                        </>
                      ) : (
                        <>
                          🔄 مزامنة البيانات
                        </>
                      )}
                    </button>

                    {/* Bulk Rate Update button - moved here */}
                    {user?.role === 'admin' && (
                      <button
                        onClick={() => {
                          // For apartments, auto-select the single room type
                          if (property.type === 'شقة' && property.roomTypes?.[0]) {
                            setBulkRateForm(prev => ({
                              ...prev,
                              roomTypeId: property.roomTypes[0].id,
                              ratePlanId: '' // User selects rate plan
                            }));
                          } else {
                            // Fallback for other property types or missing data
                            setBulkRateForm(prev => ({
                              ...prev,
                              roomTypeId: 'default',
                              ratePlanId: 'default'
                            }));
                          }
                          setShowBulkRateModal(true);
                        }}
                        className="btn btn-warning btn-sm"
                      >
                        <i className="bi bi-list-check"></i> تحديث أسعار لتواريخ متعددة
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Sync Status Message */}
              {syncRoomTypesMessage && (
                <div className={`alert ${syncRoomTypesStatus === 'error' ? 'alert-danger' : 'alert-success'} mt-3`}>
                  {syncRoomTypesMessage}
                </div>
              )}
              
              {/* Mapping Status */}
              <div className="mapping-status-card">
                <div className="status-header">
                  <h4>حالة الربط مع Channex</h4>
                  <span className={`status-badge ${channexMapping?.propertyId ? 'connected' : 'disconnected'}`}>
                    {channexMapping?.propertyId ? 'متصل' : 'غير متصل'}
                  </span>
                </div>
                
                {channexMapping?.propertyId ? (
                  <div className="mapping-info">
                    <p><strong>تاريخ الربط:</strong> {channexMapping.mappedAt ? formatDate(channexMapping.mappedAt) : 'غير متاح'}</p>
                  </div>
                ) : (
                  <p className="no-mapping-text">لم يتم ربط هذا العقار بـ Channex بعد</p>
                )}
              </div>

              {/* Configuration Notice */}
              {!channexMapping?.propertyId && (
                <div className="config-notice">
                  <div className="notice-content">
                    <h4>⚙️ إعداد الربط مطلوب</h4>
                    <p>لاستخدام ميزات Channex، يجب ربط العقار أولاً في صفحة التحرير.</p>
                    <button 
                      className="btn btn-outline-primary"
                      onClick={() => navigate(`/properties/${propertyId}/edit`)}
                    >
                      الانتقال إلى إعدادات العقار
                    </button>
                  </div>
                </div>
              )}

              {/* Sync Status and Actions */}
              <div className="sync-section">
                <h4>مزامنة التوفر</h4>
                
                <div className="sync-status">
                  <span className={`sync-indicator ${syncStatus}`}>
                    {syncStatus === 'idle' && 'جاهز للمزامنة'}
                    {syncStatus === 'syncing' && 'جاري المزامنة...'}
                    {syncStatus === 'success' && 'تمت المزامنة بنجاح'}
                    {syncStatus === 'error' && 'فشلت المزامنة'}
                  </span>
                </div>

                {syncMessage && (
                  <div className={`sync-message ${syncStatus === 'error' ? 'error' : 'success'}`}>
                    {syncMessage}
                  </div>
                )}

                <button
                  className="btn btn-secondary"
                  onClick={syncAvailability}
                  disabled={!channexMapping?.propertyId || syncStatus === 'syncing'}
                >
                  {syncStatus === 'syncing' ? 'جاري المزامنة...' : 'مزامنة التوفر'}
                </button>
              </div>

              {/* Room Types and Pricing */}
              <div className="rate-management-section">
                {getRoomTypesWithRatePlans({ ...property, channexMapping }).length > 0 ? (
                  <div className="room-types-list">
                    {getRoomTypesWithRatePlans({ ...property, channexMapping }).map((roomType, index) => (
                      <div key={roomType.id || index} className="room-type-card mb-4">
                        <div className="room-type-header">
                          <div className="room-type-badge">
                            <span className="room-icon">🏨</span>
                            {editingRoomId === roomType.id ? (
                              <div className="room-name-edit">
                                <input
                                  type="text"
                                  value={editingRoomName}
                                  onChange={(e) => setEditingRoomName(e.target.value)}
                                  className="form-control room-name-input"
                                  autoFocus
                                  disabled={roomUpdateStatus === 'syncing'}
                                />
                                <div className="edit-actions">
                                  <button 
                                    className="btn btn-success btn-sm"
                                    onClick={() => saveRoomName(roomType.id)}
                                    disabled={roomUpdateStatus === 'syncing'}
                                  >
                                    {roomUpdateStatus === 'syncing' ? 'جاري الحفظ...' : 'حفظ'}
                                  </button>
                                  <button 
                                    className="btn btn-secondary btn-sm"
                                    onClick={cancelRoomNameEdit}
                                    disabled={roomUpdateStatus === 'syncing'}
                                  >
                                    إلغاء
                                  </button>
                                </div>
                                {roomUpdateStatus === 'success' && (
                                  <small className="text-success">تم التحديث بنجاح</small>
                                )}
                              </div>
                            ) : (
                              <div className="room-name-display">
                                <h5 className="room-title">{roomType.name}</h5>
                                {user?.role === 'admin' && (
                                  <button 
                                    className="btn btn-outline-secondary btn-sm edit-room-btn"
                                    onClick={() => startRoomNameEdit(roomType)}
                                    title="تحرير اسم الغرفة"
                                  >
                                    ✏️
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <hr className="room-separator" />
                        
                        {roomType.ratePlans && roomType.ratePlans.length > 0 ? (
                          <div className="rate-plans-section">
                            <div className="rate-plans-header">
                              <span className="rate-plans-title">🏷️ خطط الأسعار</span>
                            </div>
                            
                            {/* Airbnb Rate Plan Constraint Warning */}
                            {isAirbnbProperty && roomType.ratePlans.length > 1 && (
                              <div className="alert alert-warning mb-3" role="alert">
                                <div className="d-flex align-items-center">
                                  <span className="me-2">⚠️</span>
                                  <div>
                                    <strong>تنبيه Airbnb:</strong> يدعم Airbnb خطة سعر واحدة فقط لكل عقار. 
                                    يُنصح بتحديد خطة السعر الأساسية واستخدام "السعر لكل ضيف إضافي" للاختلافات في الإشغال.
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {isAirbnbProperty && roomType.ratePlans.length === 1 && (
                              <div className="alert alert-info mb-3" role="alert">
                                <div className="d-flex align-items-center">
                                  <span className="me-2">ℹ️</span>
                                  <div>
                                    <strong>عقار Airbnb:</strong> تم اختيار خطة سعر واحدة كما هو مطلوب لـ Airbnb. 
                                    يمكنك تخصيص "السعر لكل ضيف إضافي" في إعدادات القناة.
                                  </div>
                                </div>
                              </div>
                            )}
                            <div className="rate-plans-list">
                              {roomType.ratePlans
                                .map((ratePlan, rpIndex) => (
                                <div key={ratePlan.id || rpIndex} className="rate-plan-item">
                                  {editingRatePlanId === ratePlan.id ? (
                                    <div className="rate-plan-name-edit">
                                      <input
                                        type="text"
                                        value={editingRatePlanName}
                                        onChange={(e) => setEditingRatePlanName(e.target.value)}
                                        className="form-control rate-plan-name-input"
                                        autoFocus
                                        disabled={ratePlanUpdateStatus === 'syncing'}
                                      />
                                      <div className="edit-actions">
                                        <button 
                                          className="btn btn-primary btn-sm"
                                          onClick={() => saveRatePlanName(ratePlan.id)}
                                          disabled={ratePlanUpdateStatus === 'syncing'}
                                        >
                                          {ratePlanUpdateStatus === 'syncing' ? 'جاري الحفظ...' : 'حفظ'}
                                        </button>
                                        <button 
                                          className="btn btn-secondary btn-sm"
                                          onClick={cancelRatePlanNameEdit}
                                          disabled={ratePlanUpdateStatus === 'syncing'}
                                        >
                                          إلغاء
                                        </button>
                                      </div>
                                      {ratePlanUpdateStatus === 'success' && (
                                        <small className="text-success">تم التحديث بنجاح!</small>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="rate-plan-info">
                                      <div className="rate-plan-name-display">
                                        <span className="rate-plan-name">{ratePlan.name}</span>
                                        {/* <span className="rate-plan-type">({ratePlan.type})</span> */}
                                        {user?.role === 'admin' && (
                                          <>
                                            <button 
                                              className="btn btn-outline-secondary btn-sm edit-rate-plan-btn"
                                              onClick={() => startRatePlanNameEdit(ratePlan)}
                                              title="تحرير اسم خطة السعر"
                                            >
                                              ✏️
                                            </button>
                                            <button 
                                              className="btn btn-outline-danger btn-sm delete-rate-plan-btn"
                                              onClick={() => deleteRatePlan(roomType.id, ratePlan.id)}
                                              title="حذف خطة السعر"
                                              style={{ marginLeft: '5px' }}
                                            >
                                              🗑️
                                            </button>
                                          </>
                                        )}
                                      </div>
                                      
                                      {/* Rate Display */}
                                      <div className="rate-display">
                                        {ratesLoading ? (
                                          <span className="rate-loading">
                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                            جاري التحميل...
                                          </span>
                                        ) : ratesError ? (
                                          <span className="rate-error text-danger">
                                            ❌ فشل تحميل السعر
                                          </span>
                                        ) : rates[ratePlan.id] ? (
                                          <div className="rate-info">
                                            <span className="current-rate">
                                              السعر المرجعي: {rates[ratePlan.id].baseRate} ريال
                                              <small className="text-muted ms-1">(داخلي)</small>
                                            </span>
                                            {rates[ratePlan.id].syncStatus && (
                                              <span className={`sync-status ms-2 ${rates[ratePlan.id].syncStatus}`}>
                                                {rates[ratePlan.id].syncStatus === 'synced' ? '✅' : 
                                                 rates[ratePlan.id].syncStatus === 'pending' ? '⏳' : '❌'}
                                              </span>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="rate-not-set text-muted">
                                            لم يتم تحديد السعر
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="no-rate-plans-section">
                            <p className="no-rate-plans">لا توجد خطط أسعار لهذا النوع من الغرف</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-room-types">
                    <p>لا توجد أنواع غرف مُعدة. يرجى إنشاء أنواع الغرف أولاً.</p>
                  </div>
                )}
              </div>

              {/* Full Data Sync Section - Admin Only */}
              <div className="certification-section">
                <h4>مزامنة البيانات الكاملة</h4>
                <p className="certification-description">
                  أدوات لإعداد البيانات الأولية ومزامنة الأسعار والتوفر
                </p>
                
                {/* Full Data Sync (500 Days) */}
                <div className="sync-card">
                  <div className="sync-header">
                    <h5>إعداد البيانات الأولية (500 يوم)</h5>
                    <span className="sync-status">
                      {syncTaskIds?.availability && syncTaskIds?.restrictions ? 'مكتمل' : 'جاهز'}
                    </span>
                  </div>
                  <p className="sync-description">
                    إنشاء ومزامنة 500 يوم من بيانات التوفر والأسعار/القيود للعقار
                  </p>

                  {/* Base Price Configuration */}
                  <div className="sync-config mb-3">
                    <label className="form-label">أسعار الأساس لإعداد البيانات الأولية (500 يوم)</label>
                    {/* Check if property is apartment/villa/room or hotel */}
                    {['شقة', 'فيلا', 'غرفة'].includes(property?.type) ? (
                      // Simple single price for apartments
                      <div className="price-input-section">
                        <div className="input-group mb-2">
                          <span className="input-group-text">ريال سعودي</span>
                          <input
                            type="number"
                            className="form-control text-end"
                            value={basePrice}
                            onChange={(e) => setBasePrice(parseInt(e.target.value) || 400)}
                            min="100"
                            max="5000"
                            dir="ltr"
                          />
                        </div>
                        <div className="form-text">
                          سعر أساسي للوحدة ({property?.type})
                        </div>
                      </div>
                    ) : (
                      // Multiple prices for hotels with rooms and rate plans
                      <div className="price-input-section">
                        {getRoomTypesWithRatePlans({ ...property, channexMapping }).length > 0 ? (
                          getRoomTypesWithRatePlans({ ...property, channexMapping }).map((room, roomIdx) => (
                            <div key={room.id} className="room-price-group mb-3 border p-2 rounded">
                              <h6 className="room-title">{room.name}</h6>
                              {room.ratePlans?.map((ratePlan, rpIdx) => {
                                const key = `${room.id}_${ratePlan.id}`;
                                const currentPrice = basePrices[key] || 400;

                                return (
                                  <div key={ratePlan.id} className="rate-plan-input mb-2">
                                    <div className="d-flex align-items-center">
                                      <label className="form-label small mb-0 me-3">{ratePlan.name}</label>
                                      <div className="input-group input-group-sm" style={{width: 'auto'}}>
                                        <input
                                          type="number"
                                          className="form-control text-end"
                                          value={currentPrice}
                                          onChange={(e) => updateBasePrice(room.id, ratePlan.id, e.target.value)}
                                          min="100"
                                          max="5000"
                                          dir="ltr"
                                          style={{width: '100px'}}
                                        />
                                        <span className="input-group-text">ريال</span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ))
                        ) : (
                          <div className="alert alert-info">
                            يرجى إعداد الغرف وخطط الأسعار أولاً في قسم Channex Integration
                          </div>
                        )}
                      </div>
                    )}

                    {/* Weekend Increase Input */}
                    <div className="mt-3 mb-2">
                      <label className="form-label">زيادة نهاية الأسبوع (الجمعة والسبت)</label>
                      <div className="input-group" style={{maxWidth: '250px'}}>
                        <input
                          type="number"
                          className="form-control"
                          value={weekendIncrease}
                          onChange={(e) => setWeekendIncrease(e.target.value)}
                          min="0"
                          max="100"
                          placeholder="0"
                        />
                        <span className="input-group-text">%</span>
                      </div>
                      <small className="form-text text-muted">
                        القيمة الافتراضية: 0% (لا توجد زيادة)
                      </small>
                    </div>

                    <small className="text-muted d-block">
                      البيانات ستُنشأ بالنمط التالي:
                    </small>
                    <small className="text-muted d-block">
                      • أسعار نهاية الأسبوع: {weekendIncrease > 0 ? `أعلى ${weekendIncrease}% من السعر الأساسي` : 'نفس السعر الأساسي (بدون زيادة)'}
                    </small>
                    <small className="text-muted d-block">
                      • الحد الأدنى للإقامة: يومين في نهاية الأسبوع
                    </small>
                    <small className="text-muted d-block">
                      • التوفر: {['شقة', 'فيلا', 'غرفة'].includes(property?.type) ? '0-1 وحدة' : '3-5 غرف للفنادق'}
                    </small>
                  </div>

                  {/* NEW: Unified Full Sync Button */}
                  <div className="sync-step">
                    <h6>المزامنة الكاملة (500 يوم)</h6>
                    {property.channexMapping?.initialSyncCompleted ? (
                      <div className="alert alert-success">
                        <i className="bi bi-check-circle me-2"></i>
                        <strong>المزامنة الأولية مكتملة</strong>
                        <div className="small mt-1">
                          تمت المزامنة بتاريخ: {formatDateTime(property.channexMapping.initialSyncCompletedAt || property.channexMapping.lastSyncedAt)}
                        </div>
                        <div className="small text-muted mt-1">
                          لتحديث الأسعار، استخدم "تحديث الأسعار المجمع" أعلاه
                        </div>
                      </div>
                    ) : (
                      <>
                        <button
                          className="btn btn-primary btn-lg"
                          onClick={generateAndSyncFullData}
                          disabled={syncStatus === 'syncing' || ratesLoading}
                        >
                          إعداد التقويم الأولي (مرة واحدة - 500 يوم)
                        </button>
                        {syncData?.fullData && (
                          <span className="sync-info">
                            تم: {syncData.fullData.length} سجل
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {/* Progress Indicator */}
                  {syncProgress > 0 && (
                    <div className="sync-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${syncProgress}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">{syncProgress}%</span>
                    </div>
                  )}

                  {/* Sync Results Display */}
                  {(syncTaskIds?.availability || syncTaskIds?.restrictions) && (
                    <div className="task-ids-display">
                      <h6>📋 نتائج المزامنة:</h6>
                      <div className="task-ids-list">
                        {syncTaskIds.availability && (
                          <div className="task-id-item">
                            <strong>معرف التوفر:</strong>
                            <code>{syncTaskIds.availability}</code>
                          </div>
                        )}
                        {syncTaskIds.restrictions && (
                          <div className="task-id-item">
                            <strong>معرف القيود:</strong>
                            <code>{syncTaskIds.restrictions}</code>
                          </div>
                        )}
                      </div>
                      
                      {syncTaskIds.availability && syncTaskIds.restrictions && (
                        <button 
                          className="btn btn-primary copy-btn"
                          onClick={copyTaskIds}
                        >
                          📋 نسخ Task IDs
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Test Status */}
                  {syncTaskIds?.availability && syncTaskIds?.restrictions && (
                    <div className="sync-completed">
                      <div className="success-message">
                        🎉 Test 1 مكتمل! يمكنك الآن إرسال Task IDs إلى Channex
                      </div>
                      <div className="next-steps">
                        <h6>الخطوات التالية:</h6>
                        <ul>
                          <li>التحقق من البيانات في لوحة Channex</li>
                          <li>أخذ لقطات شاشة للتوثيق</li>
                          <li>إرسال Task IDs في نموذج الشهادة</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Calendar Tab - Admin, Staff, and TurnKey Owners */}
          {activeTab === 'calendar' && (user?.role === 'admin' || user?.role === 'ops' || user?.role === 'ops-manager' || user?.role === 'maintenance' || user?.role === 'maintenance-supervisor' || user?.role === 'turnKeyOwner') && property?._id && (
            <div className="calendar-tab">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>التقويم واتاحة الحجوزات</h3>
                {/* Action buttons - Hidden from turnKey owners (read-only view) */}
                {(user?.role === 'admin' || user?.role === 'ops-manager') && (
                  <div className="d-flex gap-2">
                    {/* Price Management */}
                    <button
                      className="btn btn-primary touch-btn"
                      onClick={() => {
                        // For apartments, auto-select the single room type
                        if (property.type === 'شقة' && property.roomTypes?.[0]) {
                          setBulkRateForm(prev => ({
                            ...prev,
                            roomTypeId: property.roomTypes[0].id,
                            ratePlanId: '' // User selects rate plan
                          }));
                        } else {
                          // Fallback for other property types or missing data
                          setBulkRateForm(prev => ({
                            ...prev,
                            roomTypeId: 'default',
                            ratePlanId: 'default'
                          }));
                        }
                        setShowBulkRateModal(true);
                        setActiveModalTab('pricing');
                      }}
                    >
                      <i className="bi bi-currency-dollar me-2"></i>
                      تحديث الأسعار والقيود
                    </button>

                    {/* Sync to Channex - Moved to Step 4 in Properties Management */}
                    {/* Users should use the 4-step onboarding flow instead */}

                    {/* Import from Channex - COMMENTED OUT (kept for reference) */}
                    {/* We now use Host Me as source of truth, no import needed */}
                    {/*
                    <button
                      className="btn btn-primary"
                      onClick={handleImportFromChannex}
                      disabled={syncStatus === 'loading'}
                    >
                      <i className="bi bi-cloud-download me-2"></i>
                      {syncStatus === 'loading' ? 'جاري الاستيراد...' : 'استيراد من Channex'}
                    </button>
                    */}
                  </div>
                )}
              </div>

              {/* Sync Status Message */}
              {syncMessage && (
                <div className={`alert ${syncStatus === 'error' ? 'alert-danger' : syncStatus === 'success' ? 'alert-success' : 'alert-info'} mt-3`}>
                  {syncMessage}
                </div>
              )}

              {(calendarError || bookingsError) && (
                <div className="alert alert-warning mt-3" role="alert">
                  {lockT('unitDetails.calendarLoadError')}
                  <button type="button" className="btn btn-outline-secondary btn-sm touch-btn"
                    style={{ marginInlineStart: '12px' }}
                    onClick={() => { refetchBookings(); fetchCalendarData(calendarCurrentDate); }}>
                    {lockT('states.retry')}
                  </button>
                </div>
              )}
              {!calendarError && !bookingsError && (calendarLoading || bookingsLoading) && (
                <div className="text-muted mt-3" role="status">
                  <span className="spinner-border spinner-border-sm" style={{ marginInlineEnd: '8px' }}></span>
                  {lockT('states.loading')}
                </div>
              )}

              <div className="calendar-container">
                {/* FullCalendar for Monthly View with proper timezone support */}
                <div style={{ minHeight: '500px', direction: 'rtl' }}>
                  <FullCalendar
                    key={Object.keys(calendarData).length}
                    plugins={[dayGridPlugin, interactionPlugin, momentTimezonePlugin]}
                    initialView="dayGridMonth"
                    // Opens on the month asked for via ?date=YYYY-MM-DD, so the pricing
                    // page's 30-day list lands on the night ops was actually looking at.
                    // Omitted without the param, so FullCalendar's own default (today)
                    // applies and nothing about the existing behaviour changes.
                    {...(initialCalendarViewDate ? { initialDate: initialCalendarViewDate } : {})}
                    timeZone={BUSINESS_TIMEZONE}
                    locale="ar"
                    direction="rtl"
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: ''
                    }}
                    buttonText={{
                      today: 'اليوم',
                      month: 'شهر',
                      prev: 'السابق',
                      next: 'التالي'
                    }}
                    events={convertBookingsToEvents(calendarBookings.length > 0 ? calendarBookings : activeBookingsForCalendar)}
                    dayCellDidMount={(info) => {
                      // IMPORTANT: Use data-date attribute instead of calculating from info.date
                      // because FullCalendar uses Asia/Riyadh timezone but info.date is in browser's timezone (GMT)
                      // causing off-by-one errors. See: docs/02_Product/design_system/HOST_ME_UI_DESIGN_SYSTEM.md
                      const dateKey = info.el.getAttribute('data-date');

                      const dayData = calendarData[dateKey];
                      if (dayData && Object.keys(dayData).length > 0) {
                        const firstRoomKey = Object.keys(dayData)[0];
                        const roomData = dayData[firstRoomKey];

                        // Same resolver the modal uses, so the cell and the day it
                        // opens can never tell ops two different things. No booking
                        // list is passed: bookings already paint themselves as event
                        // bars over the cell, and this callback fires on mount only.
                        const state = resolveAvailabilityState({
                          available: roomData?.availability,
                          booked: roomData?.booked,
                          blocked: roomData?.blocked,
                          total: roomData?.total,
                          stopSell: roomData?.restrictions?.stopSell === true
                        });

                        const cellBg = availabilityView(state).cellBg;
                        if (cellBg) {
                          // setProperty(..., 'important') because .fc-day-today in
                          // PropertyDetailsStyled.css sets its tint !important, which
                          // outranks a plain inline style — so today's cell would have
                          // silently dropped its state colour and looked merely "today".
                          info.el.style.setProperty('background-color', cellBg, 'important');
                          info.el.style.opacity = '0.8';
                        }
                        // Held / sales-closed nights also get the shared diagonal line — the
                        // same one «التقويم» draws, so both calendars mark "closed" alike.
                        if (isClosedState(state)) {
                          info.el.style.setProperty('background-image', CLOSED_NIGHT_LINE, 'important');
                        }
                      }
                    }}
                    dateClick={(info) => {
                      handleDayClick(info.dateStr, info.date);
                    }}
                    eventClick={(info) => {
                      if (info.event.extendedProps?.bookingId) {
                        navigate(`/bookings/${info.event.extendedProps.bookingId}`);
                      }
                    }}
                    eventContent={(eventInfo) => {
                      return (
                        <div style={{
                          backgroundColor: eventInfo.event.backgroundColor || '#471396',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {eventInfo.event.title}
                        </div>
                      );
                    }}
                    height="auto"
                    contentHeight="auto"
                    dayMaxEvents={3}
                    moreLinkText={(num) => `+ ${num} حجز إضافي`}
                  />
                </div>

                {/* Calendar Legend */}
                <div className="calendar-legend" style={{ marginTop: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', flexWrap: 'wrap', direction: 'rtl' }}>
                    {/* Driven from the shared state map so a colour can never appear on
                        a cell without the legend explaining it. Only states that paint
                        a cell are listed — booked days show as event bars, not a wash.
                        PARTIAL is deliberately absent: it needs a room with total > 1 and
                        no property has one, so listing it would explain a colour ops will
                        never see. Add it here the day a multi-unit property is onboarded. */}
                    {[AVAILABILITY_STATE.BLOCKED, AVAILABILITY_STATE.STOP_SELL].map((state) => (
                      <div key={state} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '25px', height: '25px', backgroundColor: availabilityView(state).cellBg, backgroundImage: CLOSED_NIGHT_LINE, borderRadius: '4px', opacity: '0.8' }}></div>
                        <span>{availabilityView(state).label}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '25px', height: '25px', backgroundColor: '#FF5A5F', borderRadius: '4px' }}></div>
                      <span>Airbnb</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '25px', height: '25px', backgroundColor: '#003580', borderRadius: '4px' }}></div>
                      <span>Booking.com</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '25px', height: '25px', backgroundColor: '#E8730C', borderRadius: '4px' }}></div>
                      <span>GatherN</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '25px', height: '25px', backgroundColor: '#471396', borderRadius: '4px' }}></div>
                      <span>Host Me</span>
                    </div>
                  </div>
                </div>

                {/* OLD CALENDAR GRID REMOVED - Now using react-big-calendar above */}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Day Details Modal */}
      <Modal
        show={showDayModal}
        onHide={() => setShowDayModal(false)}
        size="lg"
        dir="rtl"
      >
        <Modal.Header closeButton style={{ direction: 'rtl', textAlign: 'right' }}>
          <Modal.Title>
            تفاصيل اليوم - {selectedDayData && formatDate(selectedDayData.date)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ direction: 'rtl', textAlign: 'right' }}>
          {selectedDayData && (
            <div>
              {/* Availability Status */}
              <div className="mb-4">
                <h5 className="mb-3">📊 حالة التوفر</h5>
                <div className="alert" style={{
                  backgroundColor: availabilityView(selectedDayData.state).bg,
                  borderColor: availabilityView(selectedDayData.state).border,
                  color: availabilityView(selectedDayData.state).text
                }}>
                  <strong>الحالة:</strong> {availabilityView(selectedDayData.state).label}
                </div>
              </div>

              {/* Pricing Information */}
              <div className="mb-4">
                <h5 className="mb-3">💰 معلومات السعر</h5>
                <table className="table table-bordered">
                  <tbody>
                    <tr>
                      <td><strong>السعر لليلة:</strong></td>
                      <td>{selectedDayData.rate} ر.س</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Restrictions */}
              <div className="mb-4">
                <h5 className="mb-3">🚫 القيود</h5>
                <table className="table table-bordered">
                  <tbody>
                    <tr>
                      <td><strong>الحد الأدنى للإقامة:</strong></td>
                      <td>{selectedDayData.minStay} ليلة</td>
                    </tr>
                    <tr>
                      <td><strong>الحد الأقصى للإقامة:</strong></td>
                      <td>{selectedDayData.maxStay} ليلة</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Bookings */}
              {selectedDayData.bookings.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3">📅 الحجوزات ({selectedDayData.bookings.length})</h5>
                  <div className="list-group">
                    {selectedDayData.bookings.map((booking, index) => (
                      <div key={index} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{booking.guestName || 'ضيف'}</strong>
                            <div className="text-muted small">
                              {booking.sourcePlatform || 'غير محدد'}
                            </div>
                          </div>
                          <div>
                            <span className="badge bg-primary">
                              {moment(booking.checkIn || booking.checkInDate || booking.startDate).format('DD/MM')} - {moment(booking.checkOut || booking.checkOutDate || booking.endDate).format('DD/MM')}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ direction: 'rtl' }} className="touch-modal-actions">
          <button
            className="btn btn-secondary touch-btn"
            onClick={() => setShowDayModal(false)}
          >
            إغلاق
          </button>
        </Modal.Footer>
      </Modal>

      {/* Review Response Modal */}
      {showReviewResponseModal && selectedReview && (
        <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">إضافة رد على التقييم</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowReviewResponseModal(false);
                    setReviewResponse('');
                    setSelectedReview(null);
                  }}
                  disabled={reviewActionLoading}
                ></button>
              </div>
              <div className="modal-body">
                {/* Original Review */}
                <div className="mb-4" style={{
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  borderRight: '3px solid #6c757d'
                }}>
                  <div className="mb-2">
                    <strong>{selectedReview.guestName}</strong>
                    <div className="mt-1">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={i < selectedReview.rating ? 'bi bi-star-fill' : 'bi bi-star'}
                          style={{color: i < selectedReview.rating ? '#FFD700' : '#ccc', fontSize: '0.9rem'}}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <p style={{margin: 0, fontSize: '0.95rem'}}>
                    {selectedReview.comment}
                  </p>
                </div>

                {/* Response Textarea */}
                <div className="mb-3">
                  <label className="form-label">ردك على التقييم:</label>
                  <textarea
                    className="form-control"
                    rows="5"
                    value={reviewResponse}
                    onChange={(e) => setReviewResponse(e.target.value)}
                    placeholder="اكتب ردك هنا..."
                    maxLength={1000}
                    disabled={reviewActionLoading}
                  ></textarea>
                  <small className="text-muted">
                    {reviewResponse.length}/1000 حرف
                  </small>
                </div>
              </div>
              <div className="modal-footer touch-modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary touch-btn"
                  onClick={() => {
                    setShowReviewResponseModal(false);
                    setReviewResponse('');
                    setSelectedReview(null);
                  }}
                  disabled={reviewActionLoading}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn btn-primary touch-btn"
                  onClick={submitReviewResponse}
                  disabled={reviewActionLoading || !reviewResponse.trim()}
                >
                  {reviewActionLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      جاري الإضافة...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send me-2"></i>
                      إرسال الرد
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Combined Price & Availability Modal */}
      {showBulkRateModal && (
        <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content bulk-rate-modal" dir="rtl">
              <div className="modal-header bulk-rate-modal__header">
                <h5 className="modal-title">
                  تحديث الأسعار والتوفر
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowBulkRateModal(false)}
                ></button>
              </div>
              <div className="modal-body bulk-rate-modal__body">
                {/* Tab Navigation */}
                <ul className="nav nav-tabs mb-4 bulk-rate-modal__tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link touch-tab ${activeModalTab === 'pricing' ? 'active' : ''}`}
                      onClick={() => setActiveModalTab('pricing')}
                    >
                      <i className="bi bi-currency-dollar me-2"></i>
                      الأسعار والقيود
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link touch-tab ${activeModalTab === 'availability' ? 'active' : ''}`}
                      onClick={() => setActiveModalTab('availability')}
                    >
                      <i className="bi bi-calendar-check me-2"></i>
                      التوفر
                    </button>
                  </li>
                </ul>

                {/* Price Tab Content */}
                <div style={{ display: activeModalTab === 'pricing' ? 'block' : 'none' }}>
                {/* Update Type Selection - Simplified */}
                <div className="update-type-selector mb-4 bulk-section">
                  <div className="bulk-section__title">نوع التحديث</div>
                  <div className="btn-group w-100" role="group">
                    <input
                      type="radio"
                      className="btn-check"
                      name="updateType"
                      id="single"
                      checked={bulkRateForm.updateType === 'single'}
                      onChange={() => setBulkRateForm({...bulkRateForm, updateType: 'single'})}
                    />
                    <label className="btn btn-outline-primary" htmlFor="single">
                      <i className="bi bi-calendar-day"></i> يوم واحد
                    </label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="updateType"
                      id="range"
                      checked={bulkRateForm.updateType === 'range'}
                      onChange={() => setBulkRateForm({...bulkRateForm, updateType: 'range'})}
                    />
                    <label className="btn btn-outline-primary" htmlFor="range">
                      <i className="bi bi-calendar-range"></i> نطاق تواريخ
                    </label>
                  </div>
                </div>

                {/* Add Rate Form */}
                <div className="bulk-rate-form mb-4 bulk-section">
                  <div className="bulk-section__title">تفاصيل التحديث</div>
                  <div className="row g-3">
                    {/* For Apartments: Only show rate plan selection (room type is auto-selected) */}
                    {/* Uses getEffectiveRoomMapping to support both single-unit and multi-unit properties */}
                    {['شقة', 'غرفة', 'فيلا', 'وحدة سكنية (شقة)', 'وحدة سكنية (فيلا)'].includes(property.type) && (
                      <div className="col-md-6">
                        <label className="form-label">خطة السعر</label>
                        <select
                          className="form-select touch-select"
                          value={bulkRateForm.ratePlanId}
                          onChange={(e) => setBulkRateForm({...bulkRateForm, ratePlanId: e.target.value})}
                        >
                          <option value="">اختر خطة السعر</option>
                          {/* Get rate plans using effective room mapping (supports multi-unit via linkedRoomTypeId) */}
                          {getEffectiveRoomMapping({ ...property, channexMapping })?.ratePlans?.map(plan => (
                            <option key={plan.id} value={plan.id}>
                              {plan.name || 'Standard Rate'}
                            </option>
                          ))}
                        </select>
                        <small className="text-muted">
                          اختر خطة السعر المطلوب تحديثها (عادي، مع إفطار، إقامة طويلة، إلخ)
                        </small>
                      </div>
                    )}
                    
                    {/* Date fields based on update type */}
                    {bulkRateForm.updateType === 'single' && (
                      <div className="col-md-6">
                        <label className="form-label">التاريخ</label>
                        <input
                          type="date"
                          className="form-control touch-input"
                          value={bulkRateForm.date}
                          onChange={(e) => setBulkRateForm({...bulkRateForm, date: e.target.value})}
                        />
                      </div>
                    )}
                    
                    
                    {bulkRateForm.updateType === 'range' && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label">من تاريخ</label>
                          <input
                            type="date"
                            className="form-control touch-input"
                            value={bulkRateForm.date}
                            onChange={(e) => {
                              const v = e.target.value;
                              setBulkRateForm(prev => ({
                                ...prev,
                                date: v,
                                // keep "To" valid: snap it to the new "From" when empty or now earlier
                                dateEnd: (!prev.dateEnd || prev.dateEnd < v) ? v : prev.dateEnd
                              }));
                            }}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">إلى تاريخ</label>
                          <input
                            type="date"
                            className="form-control touch-input"
                            min={bulkRateForm.date || undefined}
                            value={bulkRateForm.dateEnd}
                            onChange={(e) => setBulkRateForm({...bulkRateForm, dateEnd: e.target.value})}
                          />
                        </div>

                        {/* Day of Week Selector */}
                        <div className="col-12">
                          <label className="form-label">أيام الأسبوع</label>
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            {[
                              { value: 0, label: 'الأحد', short: 'ح' },
                              { value: 1, label: 'الاثنين', short: 'ن' },
                              { value: 2, label: 'الثلاثاء', short: 'ث' },
                              { value: 3, label: 'الأربعاء', short: 'ر' },
                              { value: 4, label: 'الخميس', short: 'خ' },
                              { value: 5, label: 'الجمعة', short: 'ج' },
                              { value: 6, label: 'السبت', short: 'س' }
                            ].map(day => (
                              <div key={day.value} className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`day-${day.value}`}
                                  checked={bulkRateForm.selectedDays?.includes(day.value)}
                                  onChange={(e) => {
                                    const days = bulkRateForm.selectedDays || [];
                                    const newDays = e.target.checked
                                      ? [...days, day.value]
                                      : days.filter(d => d !== day.value);
                                    setBulkRateForm({...bulkRateForm, selectedDays: newDays});
                                  }}
                                />
                                <label className="form-check-label" htmlFor={`day-${day.value}`}>
                                  {day.label}
                                </label>
                              </div>
                            ))}
                          </div>
                          <div className="d-flex gap-2 mt-2 flex-wrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setBulkRateForm({...bulkRateForm, selectedDays: [5, 6]})}
                            >
                              نهاية الأسبوع (ج، س)
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => setBulkRateForm({...bulkRateForm, selectedDays: [0, 1, 2, 3, 4]})}
                            >
                              أيام العمل
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-info"
                              onClick={() => setBulkRateForm({...bulkRateForm, selectedDays: [0, 1, 2, 3, 4, 5, 6]})}
                            >
                              جميع الأيام
                            </button>
                          </div>
                          <small className="text-muted d-block mt-2">
                            اختر أيام محددة أو استخدم الأزرار السريعة
                          </small>
                        </div>
                      </>
                    )}
                    
                    {/* What to Update Section - Show different forms based on updateType */}
                    {bulkRateForm.updateType === 'availability' ? (
                      /* Availability Update - Adapted for Property Type */
                      <div className="col-12">
                        <div className="card bg-light">
                          <div className="card-body">
                                <h6 className="card-title">
                                  <i className="bi bi-house-check text-success"></i> 
                                  {isApartment ? 'تحديث توفر الوحدة' : 'تحديث توفر الغرف'}
                                </h6>
                                <div className="alert alert-info">
                                  <i className="bi bi-info-circle"></i> 
                                  {isApartment 
                                    ? 'تحديث حالة توفر الوحدة السكنية - سيتم إرسال التحديث إلى Channex'
                                    : 'تحديث عدد الغرف المتاحة للحجز - سيتم إرسال التحديث إلى Channex'
                                  }
                                </div>
                                <div className="row g-3">
                                  <div className="col-md-6">
                                    <label className="form-label">
                                      {isApartment ? 'حالة الوحدة' : 'عدد الغرف المتاحة'}
                                    </label>
                                    {isApartment ? (
                                      <select 
                                        className="form-select"
                                        value={bulkRateForm.availableRooms || 0}
                                        onChange={(e) => setBulkRateForm({
                                          ...bulkRateForm, 
                                          availableRooms: parseInt(e.target.value) || 0,
                                          totalRooms: 1 // Always 1 for apartments
                                        })}
                                      >
                                        <option value={0}>❌ غير متاح</option>
                                        <option value={1}>✅ متاح</option>
                                      </select>
                                    ) : (
                                      <input 
                                        type="number" 
                                        className="form-control"
                                        placeholder="0" 
                                        min="0"
                                        max="50"
                                        value={bulkRateForm.availableRooms || ''} 
                                        onChange={(e) => setBulkRateForm({
                                          ...bulkRateForm, 
                                          availableRooms: parseInt(e.target.value) || 0
                                        })} 
                                      />
                                    )}
                                    <small className="text-muted">
                                      {isApartment 
                                        ? 'اختر ما إذا كانت الوحدة متاحة أم لا'
                                        : '0 = لا توجد غرف متاحة، 1+ = عدد الغرف المتاحة للحجز'
                                      }
                                    </small>
                                  </div>
                                  {!isApartment && (
                                    <div className="col-md-6">
                                      <label className="form-label">إجمالي الغرف (اختياري)</label>
                                      <input 
                                        type="number" 
                                        className="form-control"
                                        placeholder="اتركه فارغاً للاحتفاظ بالقيمة الحالية" 
                                        min="0"
                                        max="50"
                                        value={bulkRateForm.totalRooms || ''} 
                                        onChange={(e) => setBulkRateForm({
                                          ...bulkRateForm, 
                                          totalRooms: parseInt(e.target.value) || null
                                        })} 
                                      />
                                      <small className="text-muted">
                                        إجمالي الغرف في العقار (اتركه فارغاً إذا لم تكن متأكداً)
                                      </small>
                                    </div>
                                  )}
                                </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Simplified Rate Update */
                      <>
                        <div className="col-md-6">
                          <label className="form-label">السعر (ريال)</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            value={bulkRateForm.rate}
                            onChange={(e) => setBulkRateForm({...bulkRateForm, rate: e.target.value, updateRate: true})}
                            required
                          />
                          <small className="text-muted">السعر الذي سيتم تطبيقه على التواريخ المحددة</small>
                        </div>
                        {/* Optional Restrictions */}
                        <div className="col-12">
                          <hr className="my-3" />
                          <h6>القيود (اختياري)</h6>
                        </div>

                        <div className="col-md-4">
                          <label className="form-label">الحد الأدنى للإقامة</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="1"
                            min="1"
                            value={bulkRateForm.minStayArrival || 1}
                            onChange={(e) => setBulkRateForm({
                              ...bulkRateForm,
                              minStayArrival: parseInt(e.target.value) || 1,
                              updateMinStayArrival: true
                            })}
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">الحد الأقصى للإقامة</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="0 = غير محدود"
                            min="0"
                            value={bulkRateForm.maxStay || 0}
                            onChange={(e) => setBulkRateForm({
                              ...bulkRateForm,
                              maxStay: parseInt(e.target.value) || 0,
                              updateMaxStay: e.target.value !== ''
                            })}
                          />
                          <small className="text-muted">0 = غير محدود</small>
                        </div>

                        <div className="col-12 mt-3">
                          <label className="form-label fw-bold">حالة البيع (Stop Sell)</label>
                          <div className="stop-sell-radio-group">
                            {/* Option 1: Normal - No change */}
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="stopSellAction"
                                id="stopSellNone"
                                value="none"
                                checked={bulkRateForm.stopSellAction === 'none'}
                                onChange={(e) => setBulkRateForm({ ...bulkRateForm, stopSellAction: 'none' })}
                              />
                              <label className="form-check-label" htmlFor="stopSellNone" style={{ cursor: 'pointer' }}>
                                ⚪ عادي (لا تغيير)
                              </label>
                              <small className="text-muted d-block ms-4">لن يتم تغيير حالة البيع - يمكن تحديث السعر أو القيود الأخرى فقط</small>
                            </div>

                            {/* Option 2: Allow - Clear block (stopSell: false) */}
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="stopSellAction"
                                id="stopSellAllow"
                                value="allow"
                                checked={bulkRateForm.stopSellAction === 'allow'}
                                onChange={(e) => setBulkRateForm({ ...bulkRateForm, stopSellAction: 'allow' })}
                              />
                              <label className="form-check-label text-success fw-bold" htmlFor="stopSellAllow" style={{ cursor: 'pointer' }}>
                                ✅ السماح بالحجز (إلغاء المنع)
                              </label>
                              <small className="text-muted d-block ms-4">إلغاء إيقاف البيع والسماح بالحجوزات الجديدة</small>
                            </div>

                            {/* Option 3: Block - Enable stop sell (stopSell: true) */}
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="stopSellAction"
                                id="stopSellBlock"
                                value="block"
                                checked={bulkRateForm.stopSellAction === 'block'}
                                onChange={(e) => {
                                  // Show confirmation when enabling stop sell
                                  if (window.confirm(
                                    '⚠️ تأكيد إيقاف البيع\n\n' +
                                    'هل أنت متأكد من إيقاف جميع الحجوزات للتواريخ المحددة؟\n' +
                                    'سيتم منع العملاء من الحجز خلال هذه الفترة.\n\n' +
                                    'اضغط "موافق" للتأكيد أو "إلغاء" للرجوع.'
                                  )) {
                                    setBulkRateForm({ ...bulkRateForm, stopSellAction: 'block' });
                                  }
                                  // If cancelled, radio stays on previous selection (no state change)
                                }}
                              />
                              <label className="form-check-label text-danger fw-bold" htmlFor="stopSellBlock" style={{ cursor: 'pointer' }}>
                                ⛔ إيقاف البيع (منع الحجز)
                              </label>
                              <small className="text-muted d-block ms-4">منع جميع الحجوزات الجديدة (صيانة، إغلاق مؤقت)</small>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Add to List Button - Simplified */}
                    <div className="col-12 mt-3">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={addBulkRateToList}
                        disabled={
                          !bulkRateForm.date ||
                          (!bulkRateForm.rate && !bulkRateForm.minStayArrival && !bulkRateForm.maxStay && bulkRateForm.stopSellAction === 'none')
                        }
                      >
                        <i className="bi bi-plus-circle"></i> إضافة إلى القائمة
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* List of Updates */}
                <div className="bulk-rates-list">
                  <h6>
                    التحديثات المجدولة ({bulkRatesList.reduce((total, item) => total + (item.isDateRange ? item.expandedEntries?.length || item.dayCount : 1), 0)})
                  </h6>
                  {bulkRatesList.length === 0 ? (
                    <div className="text-muted text-center py-3">
                      <i className="bi bi-list"></i> لا توجد تحديثات مجدولة بعد
                    </div>
                  ) : (
                    <div className="list-group">
                      {bulkRatesList.map(item => (
                        <div key={item.id} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{item.roomTypeName}</strong>{item.ratePlanName && ` - ${item.ratePlanName}`}
                            <br />
                            <small className="text-muted">
                              {item.isBaseRate ? (
                                <>
                                  <span className="badge bg-warning text-dark me-1">مرجعي داخلي</span>
                                  {item.rate} ريال
                                </>
                              ) : item.isAvailability ? (
                                // Availability updates display
                                <>
                                  {item.isDateRange ? (
                                    <>
                                      {formatCompactDate(item.dateStart)} - {formatCompactDate(item.dateEnd)}
                                      {' '}({item.dayCount} {item.dayCount === 1 ? 'يوم' : 'أيام'})
                                      <br />
                                      <span className="badge bg-success me-1">
                                        التوفر: {item.availableRooms}{item.totalRooms ? `/${item.totalRooms}` : ''} غرفة
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      {formatCompactDate(item.date)}
                                      <br />
                                      <span className="badge bg-success me-1">
                                        التوفر: {item.availableRooms}{item.totalRooms ? `/${item.totalRooms}` : ''} غرفة
                                      </span>
                                    </>
                                  )}
                                </>
                              ) : item.isDateRange ? (
                                // Rates & restrictions date range display
                                <>
                                  {formatCompactDate(item.dateStart)} - {formatCompactDate(item.dateEnd)}
                                  {' '}({item.dayCount} {item.dayCount === 1 ? 'يوم' : 'أيام'})
                                  {item.selectedDays && item.selectedDays.length < 7 && (
                                    <>
                                      {' '}
                                      <span className="badge bg-secondary me-1">
                                        {item.selectedDays.length === 2 && item.selectedDays.includes(5) && item.selectedDays.includes(6)
                                          ? 'نهاية الأسبوع فقط'
                                          : `${item.selectedDays.length} أيام/أسبوع`}
                                      </span>
                                    </>
                                  )}
                                  <br />
                                  {/* Display selected updates */}
                                  {item.rate && <><span className="badge bg-primary me-1">سعر: {item.rate} ريال</span></>}
                                  {item.minStayArrival && <><span className="badge bg-info me-1">إقامة دنيا: {item.minStayArrival}</span></>}
                                  {item.minStayThrough && <><span className="badge bg-info me-1">إقامة متواصلة: {item.minStayThrough}</span></>}
                                  {item.maxStay !== undefined && <><span className="badge bg-info me-1">إقامة قصوى: {item.maxStay || 'غير محدود'}</span></>}
                                  {item.closedToArrival && <><span className="badge bg-warning text-dark me-1">CTA</span></>}
                                  {item.closedToDeparture && <><span className="badge bg-warning text-dark me-1">CTD</span></>}
                                  {item.stopSell !== undefined && (
                                    item.stopSell
                                      ? <><span className="badge bg-danger me-1">⛔ إيقاف البيع</span></>
                                      : <><span className="badge bg-success me-1">✅ السماح بالحجز</span></>
                                  )}
                                </>
                              ) : (
                                // Rates & restrictions single date display
                                <>
                                  {formatCompactDate(item.date)}
                                  <br />
                                  {/* Display selected updates for single date */}
                                  {item.rate && <><span className="badge bg-primary me-1">سعر: {item.rate} ريال</span></>}
                                  {item.minStayArrival && <><span className="badge bg-info me-1">إقامة دنيا: {item.minStayArrival}</span></>}
                                  {item.minStayThrough && <><span className="badge bg-info me-1">إقامة متواصلة: {item.minStayThrough}</span></>}
                                  {item.maxStay !== undefined && <><span className="badge bg-info me-1">إقامة قصوى: {item.maxStay || 'غير محدود'}</span></>}
                                  {item.closedToArrival && <><span className="badge bg-warning text-dark me-1">CTA</span></>}
                                  {item.closedToDeparture && <><span className="badge bg-warning text-dark me-1">CTD</span></>}
                                  {item.stopSell !== undefined && (
                                    item.stopSell
                                      ? <><span className="badge bg-danger me-1">⛔ إيقاف البيع</span></>
                                      : <><span className="badge bg-success me-1">✅ السماح بالحجز</span></>
                                  )}
                                </>
                              )}
                            </small>
                          </div>
                          <button 
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeFromBulkRatesList(item.id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                </div>

                {/* Availability Tab Content */}
                <div style={{ display: activeModalTab === 'availability' ? 'block' : 'none' }}>
                    {/* Warning: Availability vs Stop Sell */}
                    <div className="alert alert-warning d-flex align-items-start gap-2 mb-3" role="alert" style={{ fontSize: '0.85rem' }}>
                      <i className="bi bi-exclamation-triangle-fill mt-1" style={{ flexShrink: 0 }}></i>
                      <div>
                        <strong>تنبيه:</strong> تغيير التوفر لا يمنع الحجوزات بشكل دائم — منصات الحجز (مثل Booking.com) قد تُعيد فتح التوفر تلقائياً عند إلغاء حجز.
                        <br />
                        لحظر التواريخ بشكل مضمون، استخدم <strong>«إيقاف البيع»</strong> من تبويب <strong>«الأسعار والقيود»</strong>.
                      </div>
                    </div>
                    {/* Update Type Selection */}
                    <div className="update-type-selector mb-4">
                      <h6>نوع التحديث</h6>
                      <div className="btn-group w-100" role="group">
                        <input
                          type="radio"
                          className="btn-check"
                          name="availUpdateType"
                          id="single-avail"
                          checked={availabilityForm.updateType === 'single'}
                          onChange={() => setAvailabilityForm({...availabilityForm, updateType: 'single'})}
                        />
                        <label className="btn btn-outline-primary" htmlFor="single-avail">
                          <i className="bi bi-calendar-day me-2"></i>يوم واحد
                        </label>

                        <input
                          type="radio"
                          className="btn-check"
                          name="availUpdateType"
                          id="range-avail"
                          checked={availabilityForm.updateType === 'range'}
                          onChange={() => setAvailabilityForm({...availabilityForm, updateType: 'range'})}
                        />
                        <label className="btn btn-outline-success" htmlFor="range-avail">
                          <i className="bi bi-calendar-range me-2"></i>نطاق تواريخ
                        </label>
                      </div>
                    </div>

                    {/* Date Fields */}
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">
                          {availabilityForm.updateType === 'range' ? 'من تاريخ' : 'التاريخ'}
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={availabilityForm.date}
                          onChange={(e) => {
                            const v = e.target.value;
                            setAvailabilityForm(prev => ({
                              ...prev,
                              date: v,
                              // keep "To" valid: snap it to the new "From" when empty or now earlier
                              dateEnd: (!prev.dateEnd || prev.dateEnd < v) ? v : prev.dateEnd
                            }));
                          }}
                        />
                      </div>

                      {availabilityForm.updateType === 'range' && (
                        <div className="col-md-6">
                          <label className="form-label">إلى تاريخ</label>
                          <input
                            type="date"
                            className="form-control"
                            min={availabilityForm.date || undefined}
                            value={availabilityForm.dateEnd}
                            onChange={(e) => setAvailabilityForm({...availabilityForm, dateEnd: e.target.value})}
                          />
                        </div>
                      )}

                      {/* Availability Status for Apartments - Simple Binary */}
                      <div className="col-12">
                        <label className="form-label">حالة التوفر</label>
                        <div className="btn-group d-flex" role="group">
                          <input
                            type="radio"
                            className="btn-check"
                            name="availStatus"
                            id="available"
                            checked={availabilityForm.availableRooms === 1}
                            onChange={() => setAvailabilityForm({...availabilityForm, availableRooms: 1})}
                          />
                          <label className="btn btn-outline-success" htmlFor="available">
                            <i className="bi bi-check-circle me-2"></i>متاح
                          </label>

                          <input
                            type="radio"
                            className="btn-check"
                            name="availStatus"
                            id="blocked"
                            checked={availabilityForm.availableRooms === 0}
                            onChange={() => setAvailabilityForm({...availabilityForm, availableRooms: 0})}
                          />
                          <label className="btn btn-outline-danger" htmlFor="blocked">
                            <i className="bi bi-x-circle me-2"></i>محجوز / غير متاح
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Availability List */}
                    <div className="availability-list mt-4">
                      <h6>التحديثات المجدولة ({availabilityList.length})</h6>
                      {availabilityList.length === 0 ? (
                        <div className="text-muted text-center py-3">
                          <i className="bi bi-calendar-x"></i> لا توجد تحديثات مجدولة
                        </div>
                      ) : (
                        <div className="list-group">
                          {availabilityList.map(item => (
                            <div key={item.id} className="list-group-item d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{item.roomTypeName}</strong>
                                <br />
                                <small className="text-muted">
                                  {item.isDateRange ? (
                                    <>
                                      {formatCompactDate(item.dateStart)} - {formatCompactDate(item.dateEnd)}
                                      {' '}({item.dayCount} أيام)
                                    </>
                                  ) : (
                                    formatCompactDate(item.date)
                                  )}
                                  {' - '}
                                  <span className={`badge ${item.availableRooms > 0 ? 'bg-success' : 'bg-danger'}`}>
                                    {item.availableRooms > 0 ? 'متاح' : 'محجوز'}
                                  </span>
                                </small>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeFromAvailabilityList(item.id)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                </div>
              </div>

              {/* Combined Modal Footer */}
              <div className="modal-footer touch-modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary touch-btn"
                  onClick={() => setShowBulkRateModal(false)}
                  disabled={bulkRateStatus === 'updating' || availabilityUpdateStatus === 'updating'}
                >
                  إلغاء
                </button>

                {/* Price Tab Actions */}
                {activeModalTab === 'pricing' && (
                  <>
                    <button
                      type="button"
                      className="btn btn-primary touch-btn"
                      onClick={addBulkRateToList}
                      disabled={!bulkRateForm.date || (!bulkRateForm.rate && !bulkRateForm.minStayArrival && !bulkRateForm.maxStay)}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      إضافة إلى القائمة
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning touch-btn"
                      onClick={submitBulkRates}
                      disabled={bulkRatesList.length === 0 || bulkRateStatus === 'updating'}
                    >
                      {bulkRateStatus === 'updating' ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          جاري الإرسال...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send"></i> إرسال {bulkRatesList.reduce((total, item) => total + (item.isDateRange ? item.expandedEntries?.length || item.dayCount : 1), 0)} تحديثات
                        </>
                      )}
                    </button>
                  </>
                )}

                {/* Availability Tab Actions */}
                {activeModalTab === 'availability' && (
                  <>
                    <button
                      type="button"
                      className="btn btn-primary touch-btn"
                      onClick={addAvailabilityToList}
                      disabled={!availabilityForm.date}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      إضافة إلى القائمة
                    </button>
                    <button
                      type="button"
                      className="btn btn-success touch-btn"
                      onClick={submitAvailability}
                      disabled={availabilityList.length === 0 || availabilityUpdateStatus === 'updating'}
                    >
                      {availabilityUpdateStatus === 'updating' ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          جاري التحديث...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle"></i> تحديث {availabilityList.length} تغيير
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Cell Edit Modal */}
      {showEditModal && (
        <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  تحرير {
                    editingCell.type === 'availability' ? 'التوفر' :
                    editingCell.type === 'minStay' ? 'الحد الأدنى للإقامة' :
                    editingCell.type === 'rate' ? 'السعر' : 'القيمة'
                  }
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCancelEdit}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    <strong>نوع الغرفة:</strong> {editingCell.roomTypeName}
                  </label>
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    <strong>التاريخ:</strong> {formatDateWithDay(editingCell.date)}
                  </label>
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    {
                      editingCell.type === 'availability' ? 'عدد الغرف المتاحة' :
                      editingCell.type === 'minStay' ? 'الحد الأدنى للإقامة (بالليالي)' :
                      editingCell.type === 'rate' ? 'السعر (ريال سعودي)' : 'القيمة'
                    }
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    min={editingCell.type === 'availability' ? '0' : '1'}
                    step={editingCell.type === 'rate' ? '0.01' : '1'}
                  />
                  <div className="form-text">
                    القيمة الحالية: {editingCell.currentValue}
                  </div>
                </div>
              </div>
              <div className="modal-footer touch-modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary touch-btn"
                  onClick={handleCancelEdit}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn btn-primary touch-btn"
                  onClick={handleSaveEdit}
                  disabled={!editValue || editValue === editingCell.currentValue.toString()}
                >
                  حفظ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal - Pre-select this property */}
      <BookingModal
        show={showBookingModal}
        onHide={() => setShowBookingModal(false)}
        onBookingCreated={() => {
          setShowBookingModal(false);
          // Refetch bookings from cache instead of full page reload
          refetchBookings();
        }}
        userRole={user?.role}
        preSelectedPropertyId={property?._id}
        preSelectedProperty={property}
      />
    </div>
  );
};

export default SecurePropertyDetails;
